import axios, { AxiosInstance, AxiosError, AxiosHeaders, type InternalAxiosRequestConfig } from "axios";
import { NameValidationResponse } from "@/components/organisms";
import { getApiBaseUrl } from "@/config/apiBaseUrl";
import authService, { buildLoginUrl } from "@/services/authService";
import { createLogger } from "@/utils/logger";
import { TASK_POLL_DEADLINE_MS, taskPollDelayMs } from "@/utils/taskPolling";
import { dispatchBalanceChanged, dispatchNoBalance } from "@/utils/billingEvents";
import type { CompanyReportOptions, CompanyReportResponse } from "@/types/companyReport";
import type { FizurJob } from "@/types/fizur";
import { getFizurApiKey, isFizurHost, setFizurApiKey } from "@/utils/fizurHost";
import type {
  CreateRiskMatrixRequest,
  RiskMatrixDetail,
  RiskMatrixSchema,
  RiskMatrixSummary,
  UpdateRiskMatrixRequest,
} from "@/types/riskMatrix";
import {
  PersonData,
  DataAnalysis,
  ParserCapabilities,
  ContextModuleInfo,
  AddressValidationRequest,
  AddressValidationResponse,
  ApiModuleMetadata,
  ComplianceCommonResponse,
  ComplianceCommonResult,
  ComplianceModule,
  ComplianceStatistics,
  COMPLIANCE_MODULES,
  BUSINESS_MODULES,
  ResponseMetadata,
  RiskProfile,
  SearchFormData,
  SearchRequest,
  ArbitrationCourtsCommonResponse,
  ArbitrationCourtsRecord,
  ArbitrationCourtsStatistics,
  EfrsbBankruptcyNewResult,
  FnsDisqualifiedCommonResponse,
  FnsDisqualifiedRecord,
  DisqualifiedStatistics,
  FnsSubsidiaryDefendantResult,
  FnsTaxDebtorResult,
  FnsAccountSuspensionRecord,
  FsinCriminalWantedRecord,
  FsspCriminalWantedRecord,
  FsspDebtorWantedRecord,
  InterpolRedNoticeRecord,
  InterpolUnSanctionRecord,
  InterpolUnSanctionsStatistics,
  InterpolYellowNoticeRecord,
  InterpolYellowNoticeStatistics,
  MinjustForeignAgentsNewResult,
  MvdWantedRecord,
  GeneralCourtsCommonResponse,
  GeneralCourtsRecord,
  GeneralCourtsStatistics,
  ComplianceSearchRequest,
  PhoneValidationResponse,
  CommonResponse,
  EmailValidationResponse,
  TaxIdValidationResponse,
  SnilsValidationResponse,
  DocumentValidationResponse,
  IPAddressValidationResponse,
  OsintCapabilities,
} from "@/types/api";
import {
  OsintTaskStatusResponse,
  OsintEnrichmentResponse,
  OsintReportStatus,
  OsintReportResults,
  OsintReportExists,
} from "@/types/osint";
import type { RiskHistoryPersonQuery, RiskHistoryResponse } from "@/types/risk-history";

type UnknownRecord = Record<string, unknown>;

// === ADMIN CLIENT TYPES ===

export type ClientStatus = "ACTIVE" | "NOT_ACTIVATED" | "SUSPENDED" | "BLOCKED";

export type ManagementType = "SELF" | "EXTERNAL";

/** План авто-повторяющегося гранта для external-клиента. */
export interface ExternalPlanResponse {
  enabled: boolean;
  grant_amount: number;
  period_days: number;
  bonus_amount: number;
  last_granted_at: string | null;
  updated_at: string | null;
}

/** Демо-доступ: срок и квота проверок (GET/PUT /v2/admin/demo-accounts/{id}). */
export interface DemoAccess {
  organization_id: number;
  is_demo: boolean;
  expires_at: string | null;
  quota: number | null;
  used: number;
  remaining: number | null;
}

/** Демо-статус текущего пользователя (GET /v2/organization/demo). */
export interface DemoStatus extends Partial<DemoAccess> {
  is_demo: boolean;
  /** Демо-ключ внутри боевой организации: без срока и квоты. */
  sandbox_key?: boolean;
}

/** Демо-доступ потенциального клиента в списке админки. */
export interface DemoAccount extends DemoAccess {
  company_name: string;
  email: string;
  /** Клиент завершил регистрацию по ссылке из письма. */
  registered: boolean;
  expired: boolean;
  created_at: string | null;
}

export interface CreateDemoAccountRequest {
  company_name: string;
  email: string;
  contact_name?: string;
  days?: number;
  quota?: number;
}

export interface ExtendDemoRequest {
  days?: number;
  quota?: number;
  reset_used?: boolean;
}

export interface UpdateExternalPlanRequest {
  enabled: boolean;
  grant_amount: number;
  period_days: number;
  bonus_amount?: number;
}

export interface GrantSubscriptionRequest {
  amount: number;
  validity_days: number;
  bonus_amount?: number;
}

export interface GrantSubscriptionResponse {
  success: boolean;
  organization_id: number;
  transaction_id: number | null;
  subscription_will_be_active: boolean;
}

export interface ClientOrganizationInfo {
  id: number;
  inn: string;
  ogrn: string | null;
  kpp: string | null;
  full_name: string;
  short_name: string | null;
  address: string | null;
  status: ClientStatus;
  edo_status: string;
  entity_type: string;
  bank_account: string | null;
  bank_bik: string | null;
  bank_name: string | null;
  bank_corr_account: string | null;
  contract_number: string | null;
  contract_date: string | null;
  signing_fio: string | null;
  signing_position: string | null;
  signing_basis: string | null;
  signing_basis_document_number: string | null;
  signing_basis_document_date: string | null;
  organization_email: string | null;
  correspondence_address: string | null;
  contact_phone: string | null;
  management_type: ManagementType;
  created_at: string;
  updated_at: string;
}

export type UpdateOrganizationRequest = Partial<
  Omit<ClientOrganizationInfo, "id" | "status" | "edo_status" | "management_type" | "created_at" | "updated_at">
>;

export interface ClientInfo {
  organization: ClientOrganizationInfo;
  users_count: number;
  active_users_count: number;
  /** Текущий баланс организации (₽). null — нет аккаунта/пакета. */
  balance?: number | null;
  /** Срок действия пакета (ISO datetime). */
  balance_expires_at?: string | null;
  /** Сколько дней осталось до конца пакета. */
  days_remaining?: number | null;
}

export interface ClientsListResponse {
  clients: ClientInfo[];
  total_count: number;
}

export interface CreateOrganizationRequest {
  entity_type: "LEGAL_ENTITY" | "INDIVIDUAL_ENTREPRENEUR";
  inn: string;
  full_name: string;
  ogrn?: string;
  kpp?: string;
  address?: string;
  bank_account: string;
  bank_bik: string;
  bank_name?: string;
  bank_corr_account?: string;
  contact_email: string;
  contact_phone?: string;
  signing_fio: string;
  signing_position: string;
  signing_basis: "Устав" | "Доверенность";
  signing_basis_document_number?: string;
  signing_basis_document_date?: string;
  signing_is_manager: boolean;
}

export interface CreateOrganizationResponse {
  success: boolean;
  application_id?: number;
  organization_id?: number;
  user_id?: number;
  message?: string;
  error?: string;
  validation_errors?: Record<string, string>;
}

export interface GetClientsParams {
  limit?: number;
  offset?: number;
  status_filter?: string;
  search_query?: string;
}

// === REQUEST HISTORY TYPES ===

export interface RequestHistoryParams {
  limit?: number;
  offset?: number;
  /** Список модулей: точные ключи или префиксы категорий. */
  module_keys?: string[];
  /** Список булевых значений для фильтра по success. */
  successes?: boolean[];
  date_from?: string;
  date_to?: string;
  /** true — только запросы по API-ключу, false — только из веб-сессии */
  via_api?: boolean;
  /** Список user_id (доступен только админу клиента/staff) */
  target_user_ids?: number[];
}

export interface RequestHistoryItem {
  id: number;
  module_key: string;
  job_id: string | null;
  report_viewed: boolean;
  success: boolean | null;
  http_status_code: number | null;
  processing_time_ms: number | null;
  request_method: string;
  created_at: string;
  /** Запрос пришёл по API-ключу (true) или из веб-сессии (false) */
  via_api?: boolean;
  /** ID API-ключа, если запрос пришёл через него */
  api_key_id?: number | null;
  /** ID пользователя, выполнившего запрос */
  user_id?: number | null;
  /**
   * Было ли действующее на момент запроса согласие на обработку ПДн
   * от данного пользователя в данной организации.
   */
  consent_signed?: boolean;
}

export interface RequestHistoryResponse {
  success: boolean;
  data: {
    items: RequestHistoryItem[];
    total: number;
    limit: number;
    offset: number;
    /** Можно ли передавать target_user_id (есть права админа клиента или staff) */
    can_filter_by_user?: boolean;
  };
}

export type HistoryAggregateBucket = "hour" | "day" | "week" | "month" | "quarter" | "year";

export interface HistoryAggregateParams {
  bucket: HistoryAggregateBucket;
  date_from?: string;
  date_to?: string;
  module_keys?: string[];
  via_api?: boolean;
  target_user_ids?: number[];
}

export interface HistoryAggregateItem {
  period_start: string;
  module_key: string;
  total: number;
  success_count: number;
  failure_count: number;
  avg_processing_ms: number | null;
}

export interface HistoryAggregateResponse {
  success: boolean;
  data: {
    items: HistoryAggregateItem[];
    totals: { total: number; success: number; failure: number };
    bucket: HistoryAggregateBucket;
    can_filter_by_user?: boolean;
  };
}

// === REPORT PDF TYPES ===

export interface GeneratePdfResponse {
  status: string;
  job_id: string;
  task_id?: string;
  message: string;
}

export interface PdfStatusResponse {
  status: "generating" | "ready" | "failed" | "not_found";
  job_id: string;
  download_url?: string;
  error?: string;
}

export interface SendPdfEmailResponse {
  status: string;
  message: string;
}

// === MODULE PDF TYPES ===

export interface ModulePdfCreateResponse {
  status: string;
  pdf_task_id: string;
  module_key: string;
  message: string;
}

export interface ModulePdfStatusResponse {
  status: "generating" | "ready" | "failed" | "not_found";
  pdf_task_id: string;
  download_url?: string;
  error?: string;
}

// === NOTIFICATION SETTINGS TYPES ===

export interface NotificationSettingsResponse {
  send_report_pdf_to_email: boolean;
}

export interface NotificationSettingsUpdate {
  send_report_pdf_to_email?: boolean;
}

// === ADMIN STAFF TYPES ===

export type StaffRole = "ADMIN" | "MANAGER" | "ACCOUNTANT";
export type StaffStatus = "ACTIVE" | "INACTIVE";

export interface StaffMember {
  id: number;
  email: string;
  status: StaffStatus;
  role: StaffRole;
  last_name: string | null;
  first_name: string | null;
  middle_name: string | null;
  position: string | null;
  totp_enabled: boolean;
  created_at: string;
  updated_at: string;
  last_activity_at: string | null;
}

export interface StaffListResponse {
  staff: StaffMember[];
  total_count: number;
}

export interface GetStaffParams {
  limit?: number;
  offset?: number;
  role_filter?: StaffRole;
  status_filter?: StaffStatus;
}

export interface CreateStaffRequest {
  email: string;
  role: StaffRole;
  last_name: string;
  first_name: string;
  middle_name?: string;
  position?: string;
}

export interface CreateStaffResponse {
  success: boolean;
  user_id?: number;
  email?: string;
  onboarding_link?: string;
  onboarding_expires_at?: string;
  onboarding_email_queued?: boolean;
  onboarding_email_dispatched?: boolean;
  error?: string;
}

export interface StaffOnboardingResponse {
  success: boolean;
  user_id: number;
  onboarding_link?: string;
  onboarding_expires_at?: string;
  onboarding_email_queued?: boolean;
  onboarding_email_dispatched?: boolean;
  error?: string;
}

export interface UpdateStaffRequest {
  email?: string;
  role?: StaffRole;
  last_name?: string;
  first_name?: string;
  middle_name?: string;
  position?: string;
  status?: StaffStatus;
}

export type ResetPasswordResponse = StaffOnboardingResponse;

// === ORGANIZATION API KEYS ===

export type OrganizationApiKeyType = "USER" | "SERVICE";

export interface OrganizationApiKey {
  id: number;
  name: string;
  key_prefix: string;
  key_type: OrganizationApiKeyType;
  status: "ACTIVE" | "REVOKED";
  created_at: string;
  last_used_at?: string | null;
  expires_at?: string | null;
  revoked_at?: string | null;
  revoked_reason?: string | null;
}

export interface OrganizationApiKeyListResponse {
  success: boolean;
  total: number;
  items: OrganizationApiKey[];
}

export interface CreateOrganizationApiKeyRequest {
  name: string;
  key_type?: OrganizationApiKeyType;
  expires_at?: string;
}

export interface CreateOrganizationApiKeyResponse {
  success: boolean;
  api_key?: string;
  key?: OrganizationApiKey;
  error?: string;
}

export interface RevokeOrganizationApiKeyResponse {
  success: boolean;
  message?: string;
  error?: string;
}

// === USER KEYS (НЭП) TYPES ===

export type UserKeyAlgorithm = "RSA-2048" | "RSA-4096";
export type UserKeyStatus = "PENDING" | "ACTIVE" | "REVOKED" | "EXPIRED";

export interface UserKey {
  id: number;
  user_id: number;
  key_version: number;
  algorithm: string;
  fingerprint: string;
  status: string;
  created_at: string;
  activated_at?: string | null;
  expires_at?: string | null;
  revoked_at?: string | null;
  revoked_reason?: string | null;
}

export interface UserKeyListResponse {
  keys: UserKey[];
  total: number;
}

export interface GenerateUserKeyRequest {
  algorithm?: UserKeyAlgorithm;
}

export interface GenerateUserKeyResponse {
  success: boolean;
  key?: UserKey;
  error?: string;
}

export interface RevokeUserKeyRequest {
  reason: string;
}

export interface RevokeUserKeyResponse {
  success: boolean;
  error?: string;
}

// === CONSENTS (НЭП) TYPES ===

export type ConsentType =
  | "PERSONAL_DATA_PROCESSING"
  | "THIRD_PARTY_DATA_CHECK"
  | "CREDIT_RATING_REQUEST"
  | "BACKGROUND_CHECK";
export type ConsentStatus = "ACTIVE" | "REVOKED" | "EXPIRED";

export interface Consent {
  id: number;
  user_id: number;
  organization_id: number;
  consent_type: string;
  status: string;
  signed_at: string;
  valid_from: string;
  valid_until?: string | null;
  signer_key_fingerprint: string;
  signer_key_version: number;
  subject_fingerprint_hash?: string | null;
  /** Phase 5.2 MR2d.4: метаданные приложенного скана. */
  has_scan?: boolean;
  scan_filename?: string | null;
  scan_content_type?: string | null;
  scan_size_bytes?: number | null;
  scan_uploaded_at?: string | null;
}

export type ConsentValidityPreset = "1m" | "1y" | "until_date" | "indefinite";

/** Phase 5.2 MR2d.4: guard перед запуском проверки. */
export interface CheckConsentRequest {
  /** По умолчанию BACKGROUND_CHECK. */
  consent_type?: ConsentType;
  /** ФИО + ДР + ИНН/СНИЛС субъекта. Достаточно любого identifier-набора. */
  subject_data: Record<string, unknown>;
}

export interface CheckConsentResponse {
  has_consent: boolean;
  consent_id?: number;
  valid_until?: string;
  /** Какой identifier совпал: fio_birthdate / inn / snils. */
  matched_by?: "fio_birthdate" | "inn" | "snils";
  fingerprints?: {
    fio_birthdate: string | null;
    inn: string | null;
    snils: string | null;
  };
  /**
   * Если активного нет, но был ранее подписанный истёкший/отозванный
   * consent — дата окончания его действия (ISO). Фронт использует это
   * для модалки «согласие истекло, переподтвердите».
   */
  last_expired_at?: string | null;
}

/** Phase 5.2 MR2d.4: метаданные приложенного скана. */
export interface ConsentScanInfo {
  has_scan: boolean;
  filename?: string | null;
  content_type?: string | null;
  size_bytes?: number | null;
  uploaded_at?: string | null;
}

export interface ConsentScanDownloadResponse {
  success: boolean;
  url?: string;
  filename?: string;
  content_type?: string;
  size_bytes?: number;
  /** Срок жизни presigned URL в секундах (default 900). */
  expires_in_seconds: number;
  error?: string;
}

export interface ConsentScanDeleteResponse {
  success: boolean;
  error?: string;
}

export interface ConsentListResponse {
  consents: Consent[];
  total: number;
}

export interface SignConsentRequest {
  consent_type: ConsentType;
  consent_text: string;
  totp_code: string;
  organization_id?: number;
  subject_data?: Record<string, unknown>;
  /** Phase 5.2 MR2d.4: пресет срока (при указании переопределяет valid_until). */
  validity_preset?: ConsentValidityPreset;
  /** Явная дата окончания (для until_date или без preset). ISO 8601. */
  valid_until?: string;
}

export interface SignConsentResponse {
  success: boolean;
  consent?: Consent;
  error?: string;
}

export interface VerifyConsentResponse {
  success: boolean;
  is_valid: boolean;
  consent?: Consent;
  signer_fingerprint?: string;
  details?: string;
  error?: string;
}

export interface RevokeConsentRequest {
  totp_code: string;
}

export interface RevokeConsentResponse {
  success: boolean;
  error?: string;
}

// === MONITORING SUBSCRIPTION TYPES ===

export type MonitoringTrack = "managed" | "pull";
export type MonitoringSubscriptionStatus = "active" | "paused" | "revoked" | "shredded";
export type MonitoringRunStatus = "ok" | "error" | "pii_unavailable" | "consent_revoked";
export type MonitoringDeliveryChannel = "webhook" | "email" | "in_app";
export type MonitoringSeverity = "A_GREEN" | "B_YELLOW" | "C_ORANGE" | "D_RED";

export interface MonitoringSubscription {
  id: string;
  organization_id: number;
  subject_id: string;
  external_subject_id?: string | null;
  subject_label?: string | null;
  consent_id: number;
  track: MonitoringTrack;
  module_keys: string[];
  schedule: string;
  /** Массив правил расписания. Может содержать одно правило для legacy подписок. */
  schedules: string[];
  status: MonitoringSubscriptionStatus;
  next_run_at: string;
  last_run_at?: string | null;
  last_run_status?: MonitoringRunStatus | null;
  failure_count: number;
  webhook_url?: string | null;
  webhook_secret_version: number;
  delivery_channels: MonitoringDeliveryChannel[];
  notify_severity_min: MonitoringSeverity;
  notification_email?: string | null;
  /** Если true — воркер шлёт synthetic heartbeat event на каждом прогоне без дельт. */
  notify_even_if_unchanged: boolean;
  /**
   * Phase 5.2 MR2b: причина текущей паузы. NULL если status != paused.
   * UI выбирает flow возобновления — CONSENT_EXPIRED требует re-sign.
   */
  pause_reason?: MonitoringPauseReason | null;
  created_at: string;
  updated_at: string;
}

export type MonitoringPauseReason = "USER_REQUEST" | "CONSENT_EXPIRED" | "CONSENT_REVOKED" | "PAYMENT_LAPSED";

export interface MonitoringSubscriptionListResponse {
  items: MonitoringSubscription[];
  limit: number;
  offset: number;
}

export interface MonitoringEducationDocument {
  diploma_type?: string;
  dpo_subtype?: string;
  surname?: string;
  surname_lat?: string;
  series?: string;
  number_blank?: string;
  issue_date?: string;
  org_name?: string;
  org_ogrn?: string;
  register_number?: string;
}

export interface MonitoringPii {
  last_name: string;
  first_name: string;
  middle_name?: string;
  birth_date?: string;
  birth_place?: string;
  // Документы (одиночные)
  passport_series?: string;
  passport_number?: string;
  passport_issue_date?: string;
  inn?: string;
  snils?: string;
  drivers_license_series?: string;
  drivers_license_number?: string;
  drivers_license_issue_date?: string;
  // Контакты (множественные)
  phones?: string[];
  emails?: string[];
  addresses?: string[];
  // Документы об образовании (множественные)
  educations?: MonitoringEducationDocument[];
}

export interface CreateMonitoringSubscriptionRequest {
  consent_id: number;
  track: MonitoringTrack;
  module_keys: string[];
  schedule: string;
  /**
   * Массив правил расписания (RRULE по RFC 5545). При наличии backend
   * берёт минимальный next_run_at по всем правилам; legacy `schedule`
   * остаётся как fallback для clients, не поддерживающих массив.
   */
  schedules?: string[];
  pii?: MonitoringPii;
  external_subject_id?: string;
  subject_label?: string;
  webhook_url?: string;
  delivery_channels?: MonitoringDeliveryChannel[];
  notify_severity_min?: MonitoringSeverity;
  /**
   * Если true (по умолчанию) — после каждого успешного прогона воркер шлёт
   * synthetic HEARTBEAT event «прогон прошёл, изменений нет». false —
   * уведомления только на реальные дельты.
   */
  notify_even_if_unchanged?: boolean;
  // notification_email на backend подставляется из CurrentUser.email —
  // клиент не передаёт произвольный адрес (PII-safety).
}

/**
 * Подписка на мониторинг криптоадреса (E6): без согласия и ПДн, модуль —
 * crypto.global.address_screen. Перепроверка по расписанию, по изменениям
 * меток Threat Intelligence и по новым переводам от размеченных адресов.
 */
export interface CreateCryptoMonitoringRequest {
  subject_type: "crypto_address";
  crypto: { chain: string; address: string };
  schedule?: string;
  schedules?: string[];
  subject_label?: string;
  external_subject_id?: string;
  webhook_url?: string;
  delivery_channels?: MonitoringDeliveryChannel[];
  notify_severity_min?: MonitoringSeverity;
  notify_even_if_unchanged?: boolean;
}

/** Ответ на создание подписки: секрет вебхука приходит один раз. */
export type CreatedMonitoringSubscription = MonitoringSubscription & { webhook_secret?: string };

export interface UpdateMonitoringSubscriptionRequest {
  schedule?: string;
  schedules?: string[];
  module_keys?: string[];
  status?: MonitoringSubscriptionStatus;
  webhook_url?: string;
  subject_label?: string;
  delivery_channels?: MonitoringDeliveryChannel[];
  notify_severity_min?: MonitoringSeverity;
  notify_even_if_unchanged?: boolean;
}

/**
 * Ответ async run-now: прогон поставлен в очередь TaskIQ.
 * Реальный результат пишется воркером через 1-3 минуты — список и last-run
 * подтягиваются повторным polling'ом, не из этого ответа.
 */
export interface MonitoringRunQueuedResponse {
  run_id: string;
  subscription_id: string;
  status: "queued";
  queued_at: string;
}

export interface MonitoringEvent {
  id: string;
  subscription_id: string;
  run_id: string;
  module_key: string;
  event_type: string;
  severity: MonitoringSeverity;
  payload: Record<string, unknown>;
  detected_at: string;
  webhook_delivered: boolean;
  webhook_attempts: number;
  webhook_next_at?: string | null;
  webhook_last_error?: string | null;
}

export interface MonitoringEventsListResponse {
  items: MonitoringEvent[];
  limit: number;
  offset: number;
}

export interface MonitoringLastRunResponse {
  subscription_id: string;
  last_run_id: string | null;
  last_run_at: string | null;
  /** Map module_key → CommonResponse.to_dict() того модуля. Может быть пустым. */
  modules: Record<string, Record<string, unknown>>;
}

export interface MonitoringLastRunReportResponse {
  subscription_id: string;
  last_run_id: string | null;
  last_run_at: string | null;
  subject_label: string | null;
  basic_information: Record<string, unknown>;
  personal_documents: Record<string, unknown>;
  personal_codes: Record<string, unknown>;
  contacts: Record<string, unknown>;
  risk_assessment: Record<string, unknown>;
  modules: Record<string, Record<string, unknown>>;
}

// === SPENDING LIMIT TYPES ===

export type SpendingLimitPreset = "unlimited" | "cap_only" | "reserved" | "reserved_cap";

export interface UserSpendingLimitBody {
  preset: SpendingLimitPreset;
  reserve_per_package?: number | null;
  cap_per_package?: number | null;
}

export interface UserSpendingLimit {
  preset: SpendingLimitPreset;
  reserve_per_package: number | null;
  cap_per_package: number | null;
  frozen_amount: number;
  spent_in_package: number | null;
  cap_remaining: number | null;
  is_active: boolean;
  updated_at: string;
}

export interface ReservableInfo {
  balance: number;
  total_frozen: number;
  reservable: number;
  balance_expires_at: string | null;
  remaining_days: number;
}

export interface UserSpendingLimitResponse {
  success: boolean;
  limit?: UserSpendingLimit | null;
  reservable?: ReservableInfo | null;
  error_code?: string | null;
  error_message?: string | null;
}

// === BILLING V3 TYPES ===

export type TariffChannel = "web" | "api";

export interface ServicePackage {
  id: number;
  code: string;
  name: string;
  description: string | null;
  min_amount: number;
  max_amount: number | null;
  bonus_percent: number;
  validity_days: number;
  is_active: boolean;
  sort_order: number;
}

export interface BillingTariff {
  id: number;
  code: string;
  name: string;
  description: string | null;
  channel: TariffChannel;
  price: number;
  module_keys: string[];
  is_active: boolean;
  sort_order: number;
}

export interface RegisteredModule {
  key: string;
  name: string;
  description: string;
  category: string;
  enabled: boolean;
}

export interface RegisteredModulesListResponse {
  modules: RegisteredModule[];
  total: number;
}

export interface ServicePackageListResponse {
  packages: ServicePackage[];
  total_count: number;
}

export interface TariffListResponse {
  tariffs: BillingTariff[];
  total_count: number;
}

export interface CreateServicePackageRequest {
  code: string;
  name: string;
  description?: string;
  min_amount: number;
  max_amount?: number;
  bonus_percent: number;
  validity_days: number;
  sort_order?: number;
}

export interface UpdateServicePackageRequest {
  name?: string;
  description?: string;
  min_amount?: number;
  max_amount?: number;
  bonus_percent?: number;
  validity_days?: number;
  is_active?: boolean;
  sort_order?: number;
}

export interface CreateTariffRequest {
  code: string;
  name: string;
  description?: string;
  channel: TariffChannel;
  price: number;
  module_keys: string[];
  sort_order?: number;
}

export interface UpdateTariffRequest {
  code?: string;
  name?: string;
  description?: string;
  price?: number;
  module_keys?: string[];
  is_active?: boolean;
  sort_order?: number;
}

// === BILLING V3 CLIENT TYPES ===

export type TransactionType = "topup" | "charge" | "refund" | "adjustment" | "expiration";
export type TransactionStatus = "pending" | "completed" | "failed" | "cancelled" | "refunded";
export type InvoiceStatus = "draft" | "sent" | "paid" | "cancelled" | "expired";

export type InvoiceSource = "manual" | "auto_renewal";

export interface BillingBalanceResponse {
  balance: number;
  balance_expires_at: string | null;
  days_remaining: number | null;
  /**
   * Сумма validity_days по всем активным+queued подпискам.
   * Знаменатель для прогресс-бара (days_remaining / days_total).
   */
  days_total: number | null;
  is_active: boolean;
  overdraft_limit: number;
  overdraft_used: number;
  overdraft_available: number;
  total_available: number;
  /** Модель управления тарифом организации. EXTERNAL — read-only ЛК. */
  management_type: ManagementType;
}

export type SpendingLimitPresetCode = "unlimited" | "reserved" | "cap_only" | "reserved_cap";

export interface MySpendingLimitResponse {
  has_limits: boolean;
  preset: SpendingLimitPresetCode | null;
  reserve_per_package: number | null;
  cap_per_package: number | null;
  frozen_amount: number | null;
  spent_in_package: number | null;
  cap_remaining: number | null;
  is_active: boolean | null;
}

export interface OrgReservableInfoResponse {
  balance: number;
  total_frozen: number;
  reservable: number;
  balance_expires_at: string | null;
  remaining_days: number | null;
  has_conflict: boolean;
}

export interface ClientPackageItem {
  id: number;
  code: string;
  name: string;
  description: string | null;
  min_amount: number;
  max_amount: number | null;
  bonus_percent: number;
  validity_days: number;
}

export interface UserReserveItem {
  user_id: number;
  user_email: string;
  user_full_name: string | null;
  reserve_per_package: number;
  exceeds_new_balance: boolean;
}

export interface ProportionalPreviewItem {
  user_id: number;
  old_reserve: number;
  new_reserve: number;
  delta: number;
}

export interface PreviewCheckoutResponse {
  new_balance: number;
  new_package_price: number;
  new_package_bonus: number;
  new_package_validity_days: number;
  current_total_frozen: number;
  deficit: number;
  has_conflict: boolean;
  users_with_reserves: UserReserveItem[];
  proportional_factor: number | null;
  proportional_preview: ProportionalPreviewItem[] | null;
}

export interface CheckoutResponse {
  invoice_id: number;
  invoice_number: string;
  amount: number;
  total_will_be_credited: number;
  resolve_strategy_applied: boolean;
}

export interface ClientPackagesResponse {
  packages: ClientPackageItem[];
}

export interface ClientTariffItem {
  id: number;
  code: string;
  name: string;
  description: string | null;
  channel: TariffChannel;
  price: number;
  module_keys: string[];
}

export interface ClientTariffsResponse {
  tariffs: ClientTariffItem[];
}

export interface BillingTransactionItem {
  id: number;
  transaction_type: TransactionType;
  status: TransactionStatus;
  amount: number;
  bonus_amount: number | null;
  balance_before: number;
  balance_after: number;
  module_key: string | null;
  description: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface BillingTransactionsResponse {
  transactions: BillingTransactionItem[];
  total_count: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface CreateBillingInvoiceRequest {
  amount: number;
  due_date?: string;
  service_package_id?: number;
}

export interface CreateBillingInvoiceResponse {
  invoice_id: number;
  invoice_number: string;
  amount: number;
  bonus_amount: number;
  total_will_be_credited: number;
  due_date: string | null;
  status: InvoiceStatus;
}

// === INVOICE FLOW TYPES ===

export interface ClientInvoiceItem {
  id: number;
  invoice_number: string;
  amount: number;
  status: InvoiceStatus;
  has_pdf: boolean;
  has_payment_confirmation: boolean;
  has_upd: boolean;
  created_at: string;
  due_date: string | null;
  bonus_amount: number;
  total_will_be_credited: number;
  validity_days: number | null;
  package_name: string | null;
  source: InvoiceSource;
}

export interface ClientInvoiceListResponse {
  invoices: ClientInvoiceItem[];
  total_count: number;
}

export interface AdminInvoiceItem {
  id: number;
  account_id: number;
  invoice_number: string;
  amount: number;
  status: InvoiceStatus;
  due_date: string | null;
  transaction_id: number | null;
  invoice_pdf_path: string | null;
  payment_confirmation_path: string | null;
  upd_file_path: string | null;
  admin_comment: string | null;
  confirmed_by_user_id: number | null;
  created_by_user_id: number | null;
  bonus_percent_snapshot: number;
  bonus_amount_snapshot: number;
  validity_days_snapshot: number | null;
  package_code_snapshot: string | null;
  package_name_snapshot: string | null;
  source: InvoiceSource;
  triggered_by_subscription_id: number | null;
  created_at: string;
  updated_at: string;
  organization_id: number | null;
  organization_short_name: string | null;
  organization_full_name: string | null;
  organization_inn: string | null;
  organization_email: string | null;
  organization_entity_type: string | null;
  created_by_user_email: string | null;
  created_by_user_full_name: string | null;
}

export interface AutoRenewalConfigResponse {
  enabled: boolean;
  target_amount: number;
  trigger_balance: number | null;
  trigger_days_before_expiry: number | null;
  updated_at: string | null;
}

export interface UpdateAutoRenewalRequest {
  enabled: boolean;
  target_amount: number;
  trigger_balance: number | null;
  trigger_days_before_expiry: number | null;
}

export type PackageSubscriptionStatus = "queued" | "active" | "exhausted" | "expired" | "superseded";

export interface PackageSubscriptionItem {
  id: number;
  status: PackageSubscriptionStatus;
  package_id: number | null;
  package_code: string | null;
  package_name: string | null;
  price_paid: number;
  bonus_percent: number;
  bonus_amount: number;
  validity_days: number;
  balance_remaining: number;
  purchased_at: string;
  activated_at: string | null;
  expires_at: string | null;
  closed_at: string | null;
}

export interface PackageSubscriptionListResponse {
  active: PackageSubscriptionItem | null;
  queued: PackageSubscriptionItem[];
  closed: PackageSubscriptionItem[];
}

export interface SwitchPackageSubscriptionResponse {
  new_active_id: number;
  superseded_ids: number[];
  transferred_balance: number;
  /** Часть остатка прежней активной, сгоревшая при переключении. */
  burned_balance: number;
  expires_at: string;
}

// === SERVICE ADDONS (внешние сервисы, например VED) ===

export interface AddonItem {
  id: number;
  code: string;
  name: string;
  description: string | null;
  group_key: string;
  tier: string;
  price: number;
  validity_days: number;
}

export interface AddonsResponse {
  addons: AddonItem[];
}

export type AddonSubscriptionStatus = "queued" | "active" | "expired";

export interface AddonSubscriptionItem {
  id: number;
  addon_code: string;
  addon_name: string;
  group_key: string;
  tier: string;
  price_paid: number;
  validity_days: number;
  status: AddonSubscriptionStatus;
  purchased_at: string;
  activated_at: string | null;
  expires_at: string | null;
  closed_at: string | null;
  invoice_id: number | null;
}

export interface AddonSubscriptionsResponse {
  active: AddonSubscriptionItem[];
  queued: AddonSubscriptionItem[];
  expired: AddonSubscriptionItem[];
}

export interface PurchaseAddonResponse {
  invoice_id: number;
  invoice_number: string;
  amount: number;
  due_date: string | null;
  status: InvoiceStatus;
  addon_code: string;
  addon_name: string;
}

export interface AdminInvoiceListResponse {
  invoices: AdminInvoiceItem[];
  total_count: number;
}

// === IN-APP NOTIFICATIONS ===

export type NotificationKind =
  | "billing.low_balance"
  | "billing.invoice_issued"
  | "billing.payment_confirmed"
  | "billing.package_expiring"
  | "billing.auto_renewal_invoice_created"
  | "monitoring.alert"
  | "compliance.report_ready"
  | "platform.staff_invited"
  | "platform.password_changed"
  | "security.new_device_login";

export type NotificationSeverity = "info" | "success" | "warning" | "critical";

export type NotificationChannel = "email" | "webhook";

export interface NotificationItem {
  id: number;
  kind: NotificationKind;
  severity: NotificationSeverity;
  title: string;
  body: string | null;
  action_url: string | null;
  payload: Record<string, unknown>;
  user_id: number | null;
  organization_id: number | null;
  is_read: boolean;
  created_at: string;
  expires_at: string | null;
}

export interface NotificationListResponse {
  items: NotificationItem[];
  total_unread: number;
}

export interface NotificationUnreadCountResponse {
  count: number;
}

export interface NotificationMarkAllReadResponse {
  marked: number;
}

export interface NotificationPreferenceItem {
  kind: NotificationKind;
  extra_channels: NotificationChannel[];
}

export interface NotificationPreferencesResponse {
  items: NotificationPreferenceItem[];
}

export interface UpdateNotificationPreferenceRequest {
  kind: NotificationKind;
  extra_channels: NotificationChannel[];
  reset_to_default?: boolean;
}

// === CADASTRAL TYPES ===

/** Информация о кадастровом объекте */
export interface CadastralInfo {
  cad_num: string;
  area: string | null;
  unit: string | null;
  cad_cost: string | null;
  address: string | null;
  obj_type: string | null;
  oks_type: string | null;
  status: string | null;
}

/**
 * Ответ на асинхронное выполнение модуля через unified API.
 * POST /v2/modules/{module_key}/execute_async
 */
interface ModuleAsyncResponse {
  task_id: string;
  module_key: string;
  status: string;
  status_url: string;
  result_url: string;
}

interface V2TaskStatusResponse {
  status: string;
  error?: string;
  progress?: number;
  message?: string;
  status_message?: string;
}

function normalizeApiBaseUrl(rawBaseUrl: string): string {
  const trimmed = rawBaseUrl.trim().replace(/\/+$/, "");
  // Поддержка старых конфигов, где base URL уже включал /v2
  return trimmed.endsWith("/v2") ? trimmed.slice(0, -3) : trimmed;
}

function getStoredAuthToken(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  return localStorage.getItem("auth_token");
}

/** Конфиг запроса с пометкой о единственной повторной попытке после продления токена. */
type RetriableRequestConfig = InternalAxiosRequestConfig & { __authRetried?: boolean };

/** Запросы самой авторизации не участвуют в цикле «401 → refresh → повтор». */
function isAuthRequestUrl(url: string | undefined): boolean {
  if (!url) {
    return false;
  }
  return url.includes("/v2/auth/refresh") || url.includes("/v2/auth/login");
}

/** Уводит на форму входа, запоминая страницу, чтобы вернуть на неё после входа. */
function redirectToLogin(): void {
  if (typeof window === "undefined") {
    return;
  }
  // Уже на форме входа — повторный переход только стёр бы сохранённый redirect
  if (window.location.pathname.startsWith("/login")) {
    return;
  }
  window.location.href = buildLoginUrl(`${window.location.pathname}${window.location.search}`);
}

export class ApiError extends Error {
  public readonly httpStatus: number | undefined;
  public readonly code: string | undefined;

  constructor(message: string, httpStatus?: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.httpStatus = httpStatus;
    this.code = code;
  }
}

const NO_BALANCE_CODES = new Set([
  "INSUFFICIENT_BALANCE",
  "ACCOUNT_NOT_FOUND",
  "ACCOUNT_INACTIVE",
  "BALANCE_EXPIRED",
  "USER_LIMIT_EXCEEDED",
]);

/** Демо-проверки закончились — это не нехватка денег, баланс тут ни при чём. */
export const DEMO_QUOTA_EXCEEDED = "DEMO_QUOTA_EXCEEDED";

export function isNoBalanceError(err: unknown): err is ApiError {
  return (
    err instanceof ApiError &&
    err.code !== DEMO_QUOTA_EXCEEDED &&
    (err.httpStatus === 402 || (err.code !== undefined && NO_BALANCE_CODES.has(err.code)))
  );
}

// Одноразовый pending consent_id, подставляемый в следующий запрос как
// `X-Consent-Id`. useNepFlow после успешного ensureConsent зовёт
// setConsentForNextRequest(consent.id), и ровно одна следующая
// проверка пометится этим consent в request_history.
let _pendingConsentId: number | null = null;

/**
 * Делит строку «серия номер» паспорта на части.
 *
 * Раньше строка всегда резалась как 4+6 — по внутреннему паспорту. Заграничный
 * «45 1234567» превращался в серию 4512 и номер 34567, и модуль объявлял
 * невалидным документ, который сама же страница предлагала как пример.
 *
 * Порядок разбора: сперва уважаем пробел, поставленный человеком; если пробела
 * нет — определяем вид по длине (9 цифр — загран 2+7, иначе внутренний 4+6).
 */
export function splitPassportSeriesNumber(raw: string): { series: string; number: string } {
  const parts = raw.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return { series: parts[0].replace(/\D/g, ""), number: parts.slice(1).join("").replace(/\D/g, "") };
  }
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 9) {
    return { series: digits.slice(0, 2), number: digits.slice(2, 9) };
  }
  return { series: digits.slice(0, 4), number: digits.slice(4, 10) };
}

/**
 * Делит «серия номер» водительского удостоверения на части.
 *
 * В форме кабинета одно поле «Серия и номер ВУ» (10 цифр), а модуль ждёт
 * раздельные license_series (4) и license_number (6). Раньше уходила слитная
 * строка, и проверка ВУ из кабинета падала с 400 «Отсутствует обязательное
 * поле: license_series». Строку неподходящей длины не режем — пусть модуль
 * скажет, что не так, вместо выдуманной серии.
 */
export function splitDriverLicense(raw: string): { license_series: string; license_number: string } {
  const digits = (raw || "").replace(/\D/g, "");
  if (digits.length === 10) {
    return { license_series: digits.slice(0, 4), license_number: digits.slice(4, 10) };
  }
  return { license_series: "", license_number: raw };
}

class ApiService {
  private client: AxiosInstance;
  private baseURL: string;

  constructor(baseURL: string = "https://api.inforensic.pro") {
    this.baseURL = normalizeApiBaseUrl(baseURL);
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 120000, // Увеличено до 2 минут для медленных модулей ФССП
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Автоматически подставляем JWT и обрабатываем FormData.
    this.client.interceptors.request.use((config) => {
      // Для FormData убираем дефолтный Content-Type — axios сам поставит multipart с boundary
      if (config.data instanceof FormData) {
        delete config.headers["Content-Type"];
      }

      const fizurKey = isFizurHost() ? getFizurApiKey() : "";
      if (fizurKey) {
        config.headers = AxiosHeaders.from(config.headers).set("X-API-Key", fizurKey);
        return config;
      }

      const token = getStoredAuthToken();
      if (!token) {
        return config;
      }

      const headers = AxiosHeaders.from(config.headers);
      if (!headers.has("Authorization")) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      // Одноразовая привязка к consent.id (после ensureConsent).
      // Только POST: иначе фоновый GET заберёт метку раньше запуска проверки.
      if (_pendingConsentId !== null && config.method?.toLowerCase() === "post" && !headers.has("X-Consent-Id")) {
        headers.set("X-Consent-Id", String(_pendingConsentId));
        _pendingConsentId = null;
      }
      // Для FormData не перезаписываем Content-Type
      if (config.data instanceof FormData) {
        headers.delete("Content-Type");
      }
      config.headers = headers;

      return config;
    });

    // Интерцептор для обработки ошибок
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        // Истёкший токен сначала пробуем продлить и только потом разлогиниваем
        if (error.response?.status === 401 && isFizurHost()) {
          // На поддомене нет входа в кабинет: неверный ключ просим ввести заново.
          setFizurApiKey("");
          window.location.reload();
          throw new ApiError("Неверный ключ API", 401);
        }
        if (error.response?.status === 401) {
          const data = error.response.data as Record<string, unknown> | undefined;
          const detail = typeof data?.detail === "string" ? data.detail : "Сессия истекла";
          const config = error.config as RetriableRequestConfig | undefined;

          // Повторяем ровно один раз и никогда — сами запросы авторизации:
          // иначе 401 на refresh раскрутил бы бесконечную цепочку продлений
          const canRetry = config !== undefined && config.__authRetried !== true && !isAuthRequestUrl(config.url);

          if (canRetry && (await authService.refresh())) {
            config.__authRetried = true;
            const headers = AxiosHeaders.from(config.headers);
            headers.set("Authorization", `Bearer ${authService.getToken() ?? ""}`);
            config.headers = headers;
            return this.client.request(config);
          }

          authService.handleSessionExpired();
          redirectToLogin();
          throw new ApiError(detail, 401);
        }

        if (error.response?.data) {
          const status = error.response.status;
          const data = error.response.data as Record<string, unknown>;
          const detail = data.detail;

          // 422 — массив ошибок валидации Pydantic
          if (status === 422 && Array.isArray(detail)) {
            const messages = detail.map((err: Record<string, unknown>) => {
              const loc = Array.isArray(err.loc) ? err.loc.join(".") : "";
              return `${loc}: ${err.msg || err.message || "Ошибка валидации"}`;
            });
            throw new ApiError(`Ошибка валидации: ${messages.join("; ")}`, status);
          }

          // detail-объект {code, message} — стандартная форма HTTPException на бэке
          // (биллинг возвращает её на 402 INSUFFICIENT_BALANCE / ACCOUNT_NOT_FOUND и т.п.)
          if (detail && typeof detail === "object") {
            const detailObj = detail as { message?: string; code?: string };
            const message = detailObj.message || (data as { message?: string }).message || "Ошибка API";
            const apiErr = new ApiError(message, status, detailObj.code);
            if (isNoBalanceError(apiErr)) {
              dispatchNoBalance({ code: detailObj.code, message });
            }
            throw apiErr;
          }

          if (typeof detail === "string") {
            const apiErr = new ApiError(detail, status);
            if (isNoBalanceError(apiErr)) dispatchNoBalance({ message: detail });
            throw apiErr;
          }

          // Конверт ошибки Go: `detail` там нет, причина лежит в `error` —
          // строкой ({"error": "Согласие не найдено"}, конверт модуля) либо
          // объектом {code, message} (отказ биллинга, 402). Без этой ветки
          // любая ошибка бэкенда доезжала до пользователя как «Ошибка API»:
          // клиент с исчерпанным пакетом видел не «Недостаточно средств», а
          // безликий отказ, и не понимал, что счёт надо пополнить.
          const goError = data.error;
          if (goError && typeof goError === "object") {
            const errObj = goError as { message?: string; code?: string };
            const message = errObj.message || "Ошибка API";
            const apiErr = new ApiError(message, status, errObj.code);
            if (isNoBalanceError(apiErr)) dispatchNoBalance({ code: errObj.code, message });
            throw apiErr;
          }
          if (typeof goError === "string" && goError !== "") {
            const apiErr = new ApiError(goError, status);
            if (isNoBalanceError(apiErr)) dispatchNoBalance({ message: goError });
            throw apiErr;
          }

          const apiErr = new ApiError((data as { message?: string }).message || "Ошибка API", status);
          if (isNoBalanceError(apiErr)) dispatchNoBalance({ message: apiErr.message });
          throw apiErr;
        }
        throw new ApiError(error.message || "Ошибка сети");
      },
    );
  }

  private isObject(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
  }

  private isComplianceModuleResponse(
    payload: unknown,
  ): payload is ComplianceCommonResponse<unknown, ComplianceStatistics> {
    if (!this.isObject(payload)) {
      return false;
    }
    const moduleName = payload.module_name;
    return typeof moduleName === "string" && moduleName.startsWith("compliance.");
  }

  private buildEmptyRiskProfile(): RiskProfile {
    return {
      flags: [],
      total_score: 0,
      overall_level: "A_GREEN",
      max_risk_level: "A",
      flags_by_level: { A: 0, B: 0, C: 0, D: 0 },
      flags_by_source: {},
      risk_categories: {},
      is_high_risk: false,
      critical_flags_count: 0,
      high_risk_flags_count: 0,
    };
  }

  private isRiskLevel(value: unknown): value is RiskProfile["max_risk_level"] {
    return (
      typeof value === "string" &&
      (["D_RED", "C_ORANGE", "B_YELLOW", "A_GREEN", "D", "C", "B", "A"] as const).includes(
        value as RiskProfile["max_risk_level"],
      )
    );
  }

  private buildEmptyStatistics<TStatistics extends ComplianceStatistics>(): TStatistics {
    return {
      total_results: 0,
      unique_entities: 0,
      categories: {} as TStatistics["categories"],
      date_range: null,
    } as TStatistics;
  }

  private normalizeRiskProfile(rawRiskProfile: unknown): RiskProfile {
    if (!this.isObject(rawRiskProfile)) {
      return this.buildEmptyRiskProfile();
    }

    const fallback = this.buildEmptyRiskProfile();
    const flags = Array.isArray(rawRiskProfile.flags) ? rawRiskProfile.flags : [];
    const totalScore = typeof rawRiskProfile.total_score === "number" ? rawRiskProfile.total_score : 0;
    const maxRiskLevel = this.isRiskLevel(rawRiskProfile.max_risk_level)
      ? rawRiskProfile.max_risk_level
      : fallback.max_risk_level;
    const overallLevel = this.isRiskLevel(rawRiskProfile.overall_level)
      ? rawRiskProfile.overall_level
      : fallback.overall_level;

    return {
      ...fallback,
      ...rawRiskProfile,
      flags,
      total_score: totalScore,
      max_risk_level: maxRiskLevel,
      overall_level: overallLevel,
    };
  }

  private normalizeComplianceData<TRecord, TStatistics extends ComplianceStatistics = ComplianceStatistics>(
    rawData: unknown,
    moduleName: string,
    fallbackDataActualDate: string,
    fallbackError?: string,
  ): ComplianceCommonResult<TRecord, TStatistics> {
    const data = this.isObject(rawData) ? rawData : {};
    const results = Array.isArray(data.results) ? (data.results as TRecord[]) : [];
    const warnings = Array.isArray(data.warnings)
      ? data.warnings.filter((item): item is string => typeof item === "string")
      : [];

    const rawSearchRequest = this.isObject(data.search_request) ? data.search_request : {};
    const rawSearchRequestPartial = rawSearchRequest as unknown as Partial<SearchRequest>;
    const searchRequest: SearchRequest = {
      ...rawSearchRequestPartial,
      query: typeof rawSearchRequestPartial.query === "string" ? rawSearchRequestPartial.query : "",
    };

    const rawStatistics = this.isObject(data.statistics) ? data.statistics : {};
    const statistics: TStatistics = {
      ...this.buildEmptyStatistics<TStatistics>(),
      ...(rawStatistics as TStatistics),
      total_results: typeof rawStatistics.total_results === "number" ? rawStatistics.total_results : 0,
      unique_entities: typeof rawStatistics.unique_entities === "number" ? rawStatistics.unique_entities : 0,
      categories: this.isObject(rawStatistics.categories)
        ? (rawStatistics.categories as TStatistics["categories"])
        : ({} as TStatistics["categories"]),
    };

    return {
      found: typeof data.found === "boolean" ? data.found : false,
      total_count: typeof data.total_count === "number" ? data.total_count : 0,
      search_request: searchRequest,
      results,
      statistics,
      risk_profile: this.normalizeRiskProfile(data.risk_profile),
      source: typeof data.source === "string" ? data.source : moduleName,
      data_actual_date: typeof data.data_actual_date === "string" ? data.data_actual_date : fallbackDataActualDate,
      processing_time_ms: typeof data.processing_time_ms === "number" ? data.processing_time_ms : undefined,
      error_message: typeof data.error_message === "string" ? data.error_message : fallbackError,
      warnings,
    };
  }

  private normalizeComplianceModuleResponse<T>(payload: T): T {
    if (!this.isComplianceModuleResponse(payload)) {
      return payload;
    }

    const fallbackDate =
      typeof payload.data_actual_date === "string" ? payload.data_actual_date : new Date().toISOString().slice(0, 10);

    const normalizedData = this.normalizeComplianceData<unknown, ComplianceStatistics>(
      payload.data,
      payload.module_name,
      fallbackDate,
      payload.error,
    );

    return {
      ...payload,
      data: normalizedData,
    } as T;
  }

  // === НОВЫЕ МЕТОДЫ ДЛЯ COMPLIANCE API v2.2 ===

  /**
   * Универсальный метод для поиска в compliance-модулях.
   * Возвращает CommonResponse по API Contract 2.4.
   */
  private async searchCompliance<TRecord, TStatistics extends ComplianceStatistics = ComplianceStatistics>(
    module: ComplianceModule,
    searchData: { person_data: SearchFormData; module_options?: Record<string, unknown> },
  ): Promise<ComplianceCommonResponse<TRecord, TStatistics>> {
    // Шаг 1: Запускаем асинхронную задачу
    const taskId = await this.searchAsync(module, searchData);

    // Шаг 2: Ожидаем завершения с polling
    return this.waitForComplianceTask<ComplianceCommonResponse<TRecord, TStatistics>>(taskId);
  }

  private buildErrorCommonResponse<TRecord, TStatistics extends ComplianceStatistics = ComplianceStatistics>(
    module: string,
    personData: SearchFormData,
    errorMessage: string,
  ): ComplianceCommonResponse<TRecord, TStatistics> {
    const now = new Date();
    const isoNow = now.toISOString();
    const dataActualDate = isoNow.split("T")[0];

    const searchRequest: SearchRequest = {
      query: "",
      person_data: {
        last_name: personData.last_name || undefined,
        first_name: personData.first_name || undefined,
        middle_name: personData.middle_name || undefined,
        birth_date: personData.birth_date || undefined,
        tax_id: personData.tax_id || personData.inn || undefined,
        ssn: personData.ssn || undefined,
      },
    };

    const statistics: TStatistics = {
      total_results: 0,
      unique_entities: 0,
      categories: {} as TStatistics["categories"],
      date_range: null,
    } as TStatistics;

    const riskProfile: RiskProfile = {
      flags: [],
      total_score: 0,
      max_risk_level: "A",
    };

    const metadata: ResponseMetadata = {
      request_id: `error_${Date.now()}`,
      requested_at: isoNow,
      completed_at: isoNow,
      processing_time_ms: 0,
      data_freshness: "unknown",
    };

    const apiMetadata: ApiModuleMetadata = {
      module,
      version: "unknown",
      parser_version: "unknown",
      connection_type: "unknown",
      retry_count: 0,
    };

    return {
      status: "ERROR",
      success: false,
      module_name: module,
      data: {
        found: false,
        total_count: 0,
        search_request: searchRequest,
        results: [],
        statistics,
        risk_profile: riskProfile,
        source: "error",
        data_actual_date: dataActualDate,
        error_message: errorMessage,
      } as ComplianceCommonResult<TRecord, TStatistics>,
      data_actual_date: dataActualDate,
      metadata,
      api_metadata: apiMetadata,
      error: errorMessage,
    };
  }

  /**
   * ФССП - Розыск должников
   */
  async checkFsspDebtorWanted(personData: SearchFormData): Promise<ComplianceCommonResponse<FsspDebtorWantedRecord>> {
    try {
      return await this.searchCompliance<FsspDebtorWantedRecord>(COMPLIANCE_MODULES.FSSP_DEBTOR_WANTED, {
        person_data: personData,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return this.buildErrorCommonResponse<FsspDebtorWantedRecord>(
        COMPLIANCE_MODULES.FSSP_DEBTOR_WANTED,
        personData,
        message,
      );
    }
  }

  /**
   * ФССП - Криминальный розыск
   */
  async checkFsspCriminalWanted(
    personData: SearchFormData,
  ): Promise<ComplianceCommonResponse<FsspCriminalWantedRecord>> {
    try {
      return await this.searchCompliance<FsspCriminalWantedRecord>(COMPLIANCE_MODULES.FSSP_CRIMINAL_WANTED, {
        person_data: personData,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return this.buildErrorCommonResponse<FsspCriminalWantedRecord>(
        COMPLIANCE_MODULES.FSSP_CRIMINAL_WANTED,
        personData,
        message,
      );
    }
  }

  /**
   * ФСИН - Уголовный розыск (Database Integration)
   * Поиск в базе данных уголовного розыска ФСИН
   */
  async checkFsinCriminalWanted(
    personData: SearchFormData,
  ): Promise<ComplianceCommonResponse<FsinCriminalWantedRecord>> {
    try {
      return await this.searchCompliance<FsinCriminalWantedRecord>(COMPLIANCE_MODULES.FSIN_CRIMINAL_WANTED, {
        person_data: personData,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return this.buildErrorCommonResponse<FsinCriminalWantedRecord>(
        COMPLIANCE_MODULES.FSIN_CRIMINAL_WANTED,
        personData,
        message,
      );
    }
  }

  /**
   * Интерпол - Красные карточки
   * Поиск в базе данных международного розыска Интерпола
   */
  async checkInterpolRedNotice(searchData: {
    person_data: SearchFormData;
  }): Promise<ComplianceCommonResponse<InterpolRedNoticeRecord>> {
    try {
      return await this.searchCompliance<InterpolRedNoticeRecord>(COMPLIANCE_MODULES.INTERPOL_RED_NOTICE, searchData);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return this.buildErrorCommonResponse<InterpolRedNoticeRecord>(
        COMPLIANCE_MODULES.INTERPOL_RED_NOTICE,
        searchData.person_data,
        message,
      );
    }
  }

  /**
   * Интерпол - Санкции ООН (UN Sanctions)
   * Поиск в санкционных списках Совета Безопасности ООН
   */
  async checkInterpolUnSanctions(searchData: {
    person_data: SearchFormData;
  }): Promise<ComplianceCommonResponse<InterpolUnSanctionRecord, InterpolUnSanctionsStatistics>> {
    try {
      return await this.searchCompliance<InterpolUnSanctionRecord, InterpolUnSanctionsStatistics>(
        COMPLIANCE_MODULES.INTERPOL_UN_SANCTIONS,
        searchData,
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return this.buildErrorCommonResponse<InterpolUnSanctionRecord, InterpolUnSanctionsStatistics>(
        COMPLIANCE_MODULES.INTERPOL_UN_SANCTIONS,
        searchData.person_data,
        message,
      );
    }
  }

  /**
   * Интерпол - Жёлтые карточки (пропавшие без вести)
   * Поиск в жёлтых карточках Интерпола
   */
  async checkInterpolYellowNotice(searchData: {
    person_data: SearchFormData;
  }): Promise<ComplianceCommonResponse<InterpolYellowNoticeRecord, InterpolYellowNoticeStatistics>> {
    try {
      return await this.searchCompliance<InterpolYellowNoticeRecord, InterpolYellowNoticeStatistics>(
        COMPLIANCE_MODULES.INTERPOL_YELLOW_NOTICE,
        searchData,
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return this.buildErrorCommonResponse<InterpolYellowNoticeRecord, InterpolYellowNoticeStatistics>(
        COMPLIANCE_MODULES.INTERPOL_YELLOW_NOTICE,
        searchData.person_data,
        message,
      );
    }
  }

  /**
   * МВД - Федеральный розыск
   */
  async checkMvdWanted(personData: SearchFormData): Promise<ComplianceCommonResponse<MvdWantedRecord>> {
    try {
      return await this.searchCompliance<MvdWantedRecord>(COMPLIANCE_MODULES.MVD_WANTED, {
        person_data: personData,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return this.buildErrorCommonResponse<MvdWantedRecord>(COMPLIANCE_MODULES.MVD_WANTED, personData, message);
    }
  }

  /**
   * Минюст - Иностранные агенты
   */
  async checkMinjustForeignAgents(
    personData: SearchFormData,
  ): Promise<ComplianceCommonResponse<MinjustForeignAgentsNewResult>> {
    try {
      return await this.searchCompliance<MinjustForeignAgentsNewResult>(COMPLIANCE_MODULES.MINJUST_FOREIGN_AGENTS, {
        person_data: personData,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return this.buildErrorCommonResponse<MinjustForeignAgentsNewResult>(
        COMPLIANCE_MODULES.MINJUST_FOREIGN_AGENTS,
        personData,
        message,
      );
    }
  }

  /**
   * ФНС - Налоговые должники (старый метод - оставлен для обратной совместимости)
   */
  async checkFnsTaxDebtor(personData: SearchFormData): Promise<ComplianceCommonResponse<FnsTaxDebtorResult>> {
    try {
      return await this.searchCompliance<FnsTaxDebtorResult>(COMPLIANCE_MODULES.FNS_TAX_DEBTOR, {
        person_data: personData,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return this.buildErrorCommonResponse<FnsTaxDebtorResult>(COMPLIANCE_MODULES.FNS_TAX_DEBTOR, personData, message);
    }
  }

  /**
   * ФНС - Налоговые должники по ИНН (API Contract 2.3)
   * Использует формат person_data.tax_id для передачи ИНН
   */
  async checkFnsTaxDebtorByInn(inn: string): Promise<ComplianceCommonResponse<FnsTaxDebtorResult>> {
    const personData: SearchFormData = {
      last_name: "",
      first_name: "",
      tax_id: inn,
    };

    try {
      const taskId = await this.searchAsyncByTaxId(COMPLIANCE_MODULES.FNS_TAX_DEBTOR, {
        tax_id: inn,
      });

      return await this.waitForComplianceTask<ComplianceCommonResponse<FnsTaxDebtorResult>>(taskId);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return this.buildErrorCommonResponse<FnsTaxDebtorResult>(COMPLIANCE_MODULES.FNS_TAX_DEBTOR, personData, message);
    }
  }

  /**
   * ФНС - Приостановление операций по счетам по ИНН (API Contract 2.4)
   * Реестр действующих приостановлений операций по счетам
   * Использует формат tax_id для передачи ИНН
   */
  async checkFnsAccountSuspensionByInn(inn: string): Promise<ComplianceCommonResponse<FnsAccountSuspensionRecord>> {
    const personData: SearchFormData = {
      last_name: "",
      first_name: "",
      tax_id: inn,
    };

    try {
      const taskId = await this.searchAsyncByTaxId(COMPLIANCE_MODULES.FNS_ACCOUNT_SUSPENSION, {
        tax_id: inn,
      });

      return await this.waitForComplianceTask<ComplianceCommonResponse<FnsAccountSuspensionRecord>>(taskId);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return this.buildErrorCommonResponse<FnsAccountSuspensionRecord>(
        COMPLIANCE_MODULES.FNS_ACCOUNT_SUSPENSION,
        personData,
        message,
      );
    }
  }

  /**
   * ФНС - Субсидиарные ответчики по ИНН (API Contract 2.4)
   * Реестр лиц, привлекаемых к субсидиарной ответственности
   * Использует формат person_data.tax_id для передачи ИНН
   */
  async checkFnsSubsidiaryDefendantByInn(inn: string): Promise<ComplianceCommonResponse<FnsSubsidiaryDefendantResult>> {
    const personData: SearchFormData = {
      last_name: "",
      first_name: "",
      tax_id: inn,
    };

    try {
      const taskId = await this.searchAsyncByTaxId(COMPLIANCE_MODULES.FNS_SUBSIDIARY_DEFENDANT, {
        tax_id: inn,
      });

      return await this.waitForComplianceTask<ComplianceCommonResponse<FnsSubsidiaryDefendantResult>>(taskId);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return this.buildErrorCommonResponse<FnsSubsidiaryDefendantResult>(
        COMPLIANCE_MODULES.FNS_SUBSIDIARY_DEFENDANT,
        personData,
        message,
      );
    }
  }

  /**
   * ФНС - Дисквалифицированные лица
   */
  async checkFnsDisqualified(personData: SearchFormData): Promise<FnsDisqualifiedCommonResponse> {
    try {
      return await this.searchCompliance<FnsDisqualifiedRecord, DisqualifiedStatistics>(
        COMPLIANCE_MODULES.FNS_DISQUALIFIED,
        {
          person_data: personData,
        },
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return this.buildErrorCommonResponse<FnsDisqualifiedRecord>(
        COMPLIANCE_MODULES.FNS_DISQUALIFIED,
        personData,
        message,
      );
    }
  }

  /**
   * Общие суды
   */
  async checkGeneralCourts(
    personData: SearchFormData,
    options: { withAI?: boolean } = {},
  ): Promise<GeneralCourtsCommonResponse> {
    try {
      return await this.searchCompliance<GeneralCourtsRecord, GeneralCourtsStatistics>(
        COMPLIANCE_MODULES.GENERAL_COURTS,
        {
          person_data: personData,
          // Карточки дел СОЮ модуль грузит всегда; по запросу добавляется
          // резюме решения от языковой модели — см. checkArbitrationCourts.
          module_options: { include_decisions: options.withAI === true },
        },
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return this.buildErrorCommonResponse<GeneralCourtsRecord, GeneralCourtsStatistics>(
        COMPLIANCE_MODULES.GENERAL_COURTS,
        personData,
        message,
      );
    }
  }

  /**
   * Арбитражные суды
   */
  async checkArbitrationCourts(
    personData: SearchFormData,
    options: { withAI?: boolean } = {},
  ): Promise<ArbitrationCourtsCommonResponse> {
    try {
      return await this.searchCompliance<ArbitrationCourtsRecord, ArbitrationCourtsStatistics>(
        COMPLIANCE_MODULES.ARBITRATION_COURTS,
        {
          person_data: personData,
          // Карточки дел (инстанции, движение, ссылки на акты) берём всегда:
          // страница рисует «Хронологию дела», а без этой опции модуль отдаёт
          // только список, и блок оставался пустым. Плата — около десяти
          // секунд.
          //
          // Разбор актов языковой моделью — по запросу: он скачивает PDF,
          // гоняет OCR и обращается к модели на каждую инстанцию. В
          // комплексной проверке его включает тариф с ИИ-анализом, здесь —
          // переключатель на странице.
          module_options: { include_details: true, include_decisions: options.withAI === true },
        },
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return this.buildErrorCommonResponse<ArbitrationCourtsRecord, ArbitrationCourtsStatistics>(
        COMPLIANCE_MODULES.ARBITRATION_COURTS,
        personData,
        message,
      );
    }
  }

  /**
   * ЕФРСБ - Банкротство
   * Использует API Standard 2.3 формат с person_data
   */
  async checkEfrsbBankruptcy(personData: SearchFormData): Promise<ComplianceCommonResponse<EfrsbBankruptcyNewResult>> {
    // API Standard 2.3: всегда используем person_data с tax_id (ИНН)
    const unifiedPersonData: SearchFormData = {
      ...personData,
      tax_id: personData.inn || personData.tax_id,
    };

    try {
      return await this.searchCompliance<EfrsbBankruptcyNewResult>(COMPLIANCE_MODULES.EFRSB_BANKRUPTCY, {
        person_data: unifiedPersonData,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return this.buildErrorCommonResponse<EfrsbBankruptcyNewResult>(
        COMPLIANCE_MODULES.EFRSB_BANKRUPTCY,
        unifiedPersonData,
        message,
      );
    }
  }

  /**
   * Анализ данных персоны
   */
  async analyzePersonData(personData: PersonData): Promise<DataAnalysis> {
    // Очищаем данные: удаляем пустые строки и undefined значения
    const cleanedData = Object.fromEntries(
      Object.entries(personData).filter(([, value]) => value !== undefined && value !== "" && value !== null),
    ) as PersonData;

    // Удаляем неполные данные паспорта (серия 4 цифры, номер 6 цифр)
    if (cleanedData.passport_series?.length !== 4 || cleanedData.passport_number?.length !== 6) {
      delete cleanedData.passport_series;
      delete cleanedData.passport_number;
      delete cleanedData.passport_issue_date;
    }

    // Удаляем неполные данные ВУ (серия 4 символа, номер 6 цифр)
    if (cleanedData.drivers_license_series?.length !== 4 || cleanedData.drivers_license_number?.length !== 6) {
      delete cleanedData.drivers_license_series;
      delete cleanedData.drivers_license_number;
      delete cleanedData.drivers_license_issue_date;
    }

    // Удаляем неполный ИНН (12 цифр для физлица)
    if (cleanedData.tax_id && cleanedData.tax_id.length !== 12) {
      delete cleanedData.tax_id;
    }

    // Удаляем неполный СНИЛС (11 цифр)
    const snilsDigits = cleanedData.ssn?.replace(/\D/g, "");
    if (snilsDigits && snilsDigits.length !== 11) {
      delete cleanedData.ssn;
    }

    // Удаляем неполные телефоны (формат +7 (XXX) XXX-XX-XX = 18 символов)
    if (cleanedData.phones) {
      cleanedData.phones = cleanedData.phones.filter((p) => p && p.length >= 18);
      if (cleanedData.phones.length === 0) delete cleanedData.phones;
    }
    if (cleanedData.phone_number && cleanedData.phone_number.length < 18) {
      delete cleanedData.phone_number;
    }

    // Удаляем неполные email (должен содержать @)
    if (cleanedData.emails) {
      cleanedData.emails = cleanedData.emails.filter((e) => e && e.includes("@"));
      if (cleanedData.emails.length === 0) delete cleanedData.emails;
    }
    if (cleanedData.email && !cleanedData.email.includes("@")) {
      delete cleanedData.email;
    }

    const response = await this.client.post<DataAnalysis>("/v2/context/analyze", {
      person_data: cleanedData,
    });
    // Backend возвращает данные напрямую (не в обёртке {data: ...})
    return response.data;
  }

  /**
   * Получение информации о возможностях системы
   */
  /**
   * Реестр модулей платформы: GET /v2/modules.
   *
   * Единственный источник правды о том, что доступно ключу организации,
   * включая международные модули. Каталог в интерфейсе строится по этому
   * ответу, поэтому новая юрисдикция появляется без правок фронтенда.
   */
  async listModules(): Promise<CatalogModule[]> {
    const response = await this.client.get<{ modules?: CatalogModule[] }>("/v2/modules");
    return response.data.modules ?? [];
  }

  async getCapabilities(): Promise<ContextModuleInfo> {
    const response = await this.client.get<ContextModuleInfo>("/v2/");
    return response.data;
  }

  /**
   * Запуск асинхронной задачи поиска исполнительных производств ФССП (только запуск)
   * Использует unified modules API: /v2/modules/{module_key}/execute_async
   */
  async startFSSPIPDebtorSearch(personData: PersonData): Promise<string> {
    return this.executeModuleAsync(COMPLIANCE_MODULES.FSSP_IP_DEBTOR, personData);
  }

  /**
   * Запуск асинхронной задачи поиска нарушений арбитражных управляющих ФНС (только запуск)
   * Использует unified modules API: /v2/modules/{module_key}/execute_async
   */
  async startFNSAUViolationsSearch(request: ComplianceSearchRequest): Promise<string> {
    const inputData = request.person_data || {};
    const options = request.module_options || {};
    return this.executeModuleAsync(COMPLIANCE_MODULES.FNS_AU_VIOLATIONS, inputData, options);
  }

  /**
   * Запуск асинхронной задачи поиска дисквалифицированных лиц ФНС (только запуск)
   * Использует unified modules API: /v2/modules/{module_key}/execute_async
   */
  async startFnsDisqualifiedSearch(
    data: {
      last_name: string;
      first_name: string;
      middle_name: string;
      birth_date: string;
    },
    opts: { includeExpired?: boolean } = {},
  ): Promise<string> {
    // include_expired — опция модуля: без неё реестр не отдаёт записи с
    // истёкшим сроком дисквалификации, и проверка по такому субъекту
    // выглядела как «ничего не найдено».
    return this.executeModuleAsync(
      COMPLIANCE_MODULES.FNS_DISQUALIFIED,
      data,
      opts.includeExpired ? { include_expired: true } : {},
    );
  }

  /**
   * Запуск асинхронной задачи поиска должников по алиментам ФССП (только запуск)
   * Использует unified modules API: /v2/modules/{module_key}/execute_async
   */
  async startFSSPAlimonyDebtorSearch(personData: PersonData): Promise<string> {
    return this.executeModuleAsync(COMPLIANCE_MODULES.FSSP_ALIMONY_DEBTOR, personData);
  }

  /**
   * Запуск асинхронной задачи проверки в реестре контролируемых лиц МВД (только запуск)
   * Использует unified modules API: /v2/modules/{module_key}/execute_async
   */
  async startMVDControlledPersonsSearch(data: {
    full_name: string;
    birth_date: string;
    foreign_passport: string;
    document_issue_date: string;
  }): Promise<string> {
    return this.executeModuleAsync(COMPLIANCE_MODULES.MVD_CONTROLLED_PERSONS, data);
  }

  /**
   * Запуск асинхронной задачи поиска кредитного рейтинга (только запуск)
   * Использует unified modules API: /v2/modules/{module_key}/execute_async
   */
  async startCreditRatingSearch(personData: PersonData): Promise<string> {
    return this.executeModuleAsync(COMPLIANCE_MODULES.CREDIT_RATING, personData);
  }

  /**
   * Запуск асинхронной задачи поиска кредитного скоринга (только запуск)
   * Использует unified modules API: /v2/modules/{module_key}/execute_async
   */
  async startCreditScoringSearch(request: ComplianceSearchRequest): Promise<string> {
    const inputData = request.person_data || {};
    const options = request.module_options || {};
    return this.executeModuleAsync(COMPLIANCE_MODULES.CREDIT_SCORING, inputData, options);
  }

  /**
   * Запуск асинхронной задачи поиска в общих судах (только запуск)
   * Использует unified modules API: /v2/modules/{module_key}/execute_async
   */
  async startGeneralCourtsSearch(personData: PersonData, options: { withAI?: boolean } = {}): Promise<string> {
    // Карточки дел модуль грузит всегда; резюме решения языковой моделью —
    // по запросу (см. checkArbitrationCourts о цене разбора).
    return this.executeModuleAsync(COMPLIANCE_MODULES.GENERAL_COURTS, personData, {
      include_decisions: options.withAI === true,
    });
  }

  /**
   * Запуск асинхронной задачи поиска в арбитражных судах (только запуск)
   * Использует unified modules API: /v2/modules/{module_key}/execute_async
   */
  async startArbitrationCourtsSearch(
    personData?: PersonData,
    legalData?: { tax_id?: string; company_name?: string },
    options: { withAI?: boolean } = {},
  ): Promise<string> {
    const inputData: Record<string, unknown> = {};

    if (personData) {
      Object.assign(inputData, personData);
    }
    if (legalData) {
      inputData.tax_id = legalData.tax_id;
      inputData.company_name = legalData.company_name;
    }

    return this.executeModuleAsync(COMPLIANCE_MODULES.ARBITRATION_COURTS, inputData, {
      include_details: true,
      include_decisions: options.withAI === true,
    });
  }

  /**
   * Получение статуса задачи
   */
  async getTaskStatus(taskId: string): Promise<V2TaskStatusResponse> {
    const response = await this.client.get<V2TaskStatusResponse>(`/v2/task/${taskId}/status`);
    return response.data;
  }

  /**
   * Получить полный API ответ с метаданными (CommonResponse формат).
   *
   * Бэкенд возвращает CommonResponse:
   *   { status, success, module_name, data: { found, results, risk_profile, ... }, metadata, ... }
   *
   * Возвращает response.data как есть - без изменений.
   * Доступ к данным: response.data.risk_profile, response.data.results и т.д.
   */
  async getTaskResultFull<TRecord = object, TStatistics extends ComplianceStatistics = ComplianceStatistics>(
    taskId: string,
  ): Promise<ComplianceCommonResponse<TRecord, TStatistics>> {
    const response = await this.client.get<ComplianceCommonResponse<TRecord, TStatistics>>(`/v2/task/${taskId}/result`);
    return this.normalizeComplianceModuleResponse(response.data);
  }

  // === ВАЛИДАЦИЯ ===

  /**
   * Валидация имени (NameDE) - sync
   * API Contract 2.4: использует person_data.full_name
   */
  /**
   * Унифицированный вызов validation-модуля через /v2/modules/{key}/execute.
   * Ответ приводится к legacy-форме CommonResponse<T>: envelope берётся из
   * unified-ответа, а data = results[0] (запись DE) + производные поля через
   * reshape. Так страницы не меняются, но фронт ходит только в unified API.
   */
  private async runValidationModule<T>(
    moduleKey: string,
    inputData: Record<string, unknown>,
    options: Record<string, unknown> = {},
    reshape?: (record: Record<string, unknown>) => unknown,
  ): Promise<CommonResponse<T>> {
    const response = await this.client.post<CommonResponse<{ results?: unknown[] }>>(
      `/v2/modules/${moduleKey}/execute`,
      { input_data: inputData, options },
    );
    const env = response.data;
    const results = env?.data?.results;
    const record = (Array.isArray(results) && results.length > 0 ? results[0] : {}) as Record<string, unknown>;
    const data = reshape ? reshape(record) : record;
    // возможное изменение баланса (как в executeModuleSync)
    dispatchBalanceChanged();
    return { ...env, data } as unknown as CommonResponse<T>;
  }

  async validateName(request: {
    full_name: string;
    normalize_input?: boolean;
    include_declension?: boolean;
    include_validation?: boolean;
    gender_hint?: string;
  }): Promise<NameValidationResponse> {
    return (await this.runValidationModule(
      "validation.rus.name",
      { full_name: request.full_name },
      {
        normalize_input: request.normalize_input,
        include_declension: request.include_declension,
        include_validation: request.include_validation,
        gender_hint: request.gender_hint || undefined,
      },
    )) as unknown as NameValidationResponse;
  }

  /**
   * Валидация телефонного номера (PhoneDE) — unified validation.phone.
   */
  async validatePhone(request: { phone: string; enable_db?: boolean }): Promise<PhoneValidationResponse> {
    return (await this.runValidationModule("validation.global.phone", { phone: request.phone }, {}, (rec) => {
      rec.status_check_timestamp = null;
      rec.osint_data = null;
      rec.osint_lookup = null;
      return rec;
    })) as unknown as PhoneValidationResponse;
  }

  /**
   * Валидация email адреса (EmailDE) — unified validation.email.
   */
  async validateEmail(request: {
    email: string;
    check_mx?: boolean;
    check_existence?: boolean;
    normalize?: boolean;
    remove_dots?: boolean;
    remove_plus?: boolean;
  }): Promise<EmailValidationResponse> {
    return (await this.runValidationModule(
      "validation.global.email",
      { email: request.email },
      { check_mx: request.check_mx },
      (rec) => {
        const domainType = rec.domain_type;
        const mx = rec.mx_domains;
        const validFmt = rec.valid_format === true;
        rec.is_valid_format = validFmt;
        rec.has_mx = Array.isArray(mx) && mx.length > 0;
        rec.is_disposable = domainType === "DISPOSAL";
        rec.is_public = domainType === "PUBLIC";
        rec.is_corporate = domainType === "NONPUBLIC";
        rec.exists = null;
        rec.existance_timestamp = null;
        rec.success = validFmt;
        return rec;
      },
    )) as unknown as EmailValidationResponse;
  }

  /**
   * Валидация ИНН (TaxIdDE) — unified validation.tax_id.
   */
  async validateTaxId(request: {
    tax_id: string;
    normalize?: boolean;
    include_region_info?: boolean;
    include_fns_inspection_info?: boolean;
    strict_validation?: boolean;
  }): Promise<TaxIdValidationResponse> {
    return (await this.runValidationModule("validation.rus.tax_id", { tax_id: request.tax_id }, {}, (rec) => {
      const valid = rec.valid === true;
      const issues = rec.issues;
      rec.is_valid = valid;
      rec.validity = valid;
      rec.has_issues = Array.isArray(issues) && issues.length > 0;
      // модуль отдаёт kind (individual/legal_entity); страница читает type
      rec.type = rec.kind;
      rec.fns_inspection_code = null;
      rec.fns_inspection = null;
      rec.fns_inspection_address = null;
      return rec;
    })) as unknown as TaxIdValidationResponse;
  }

  /**
   * Валидация СНИЛС (SnilsDE) — unified validation.social_security_number.
   */
  async validateSnils(request: {
    social_security_number: string;
    normalize?: boolean;
    include_info?: boolean;
    strict_validation?: boolean;
  }): Promise<SnilsValidationResponse> {
    return (await this.runValidationModule(
      "validation.rus.social_security_number",
      { snils: request.social_security_number },
      {},
      (rec) => ({
        source: rec.source,
        value: rec.value,
        type: "СНИЛС",
        validity: rec.valid === true,
        verified: null,
        verification_timestamp: null,
      }),
    )) as unknown as SnilsValidationResponse;
  }

  /**
   * Валидация документа (паспорт) — unified validation.document.
   * Серия/номер извлекаются из строки паспорта (первые 4 / следующие 6 цифр).
   */
  async validateDocument(request: {
    internal_passport?: { document: string; issue_date?: string; issuer?: string };
    international_passport?: { document: string; issue_date?: string; issuer?: string };
    include_region_info?: boolean;
    include_year_info?: boolean;
    include_series_info?: boolean;
  }): Promise<DocumentValidationResponse> {
    const raw = request.internal_passport?.document || request.international_passport?.document || "";
    const { series, number } = splitPassportSeriesNumber(raw);
    return (await this.runValidationModule("validation.rus.document", { series, number }, {}, (rec) => {
      rec.is_valid = rec.valid === true;
      // Название региона приходит с бэкенда (справочник по коду серии);
      // раньше оно здесь обнулялось, и страница показывала только код.
      rec.region_name = typeof rec.region_name === "string" && rec.region_name !== "" ? rec.region_name : null;
      return rec;
    })) as unknown as DocumentValidationResponse;
  }

  /**
   * Валидация IP-адреса (IPAddressDE) — unified validation.ip_address.
   */
  async validateIpAddress(request: {
    ip_address: string;
    include_whois?: boolean;
    include_geolocation?: boolean;
  }): Promise<IPAddressValidationResponse> {
    return (await this.runValidationModule(
      "validation.global.ip_address",
      { ip_address: request.ip_address },
      {},
      (rec) => {
        rec.is_public = rec.is_global === true;
        rec.has_geolocation = false;
        rec.has_whois = false;
        for (const k of [
          "country",
          "country_name",
          "city",
          "city_id",
          "latitude",
          "longitude",
          "timezone",
          "provider",
          "provider_type",
          "organization",
          "network",
          "whois_info",
          "whois_parsed",
          "last_updated",
          "confidence",
        ]) {
          rec[k] = null;
        }
        return rec;
      },
    )) as unknown as IPAddressValidationResponse;
  }

  /**
   * Валидация одиночного адреса — unified validation.address.
   *
   * Конверт (original_address / validation_result / address / metrics /
   * processing) целиком приходит с бэкенда — здесь он раньше собирался
   * заново из одной записи results. Перекладывание дважды стоило нам двух
   * дефектов подряд: сперва все метрики печатались нулями, потом нулём
   * печаталось время обработки. Отдаём то, что прислал модуль.
   */
  async validateAddress(request: AddressValidationRequest): Promise<AddressValidationResponse> {
    const env = await this.executeModuleSync<CommonResponse<unknown>>("validation.rus.address", {
      address: request.address,
    });
    return env as unknown as AddressValidationResponse;
  }

  // === СИСТЕМНЫЕ МЕТОДЫ ===

  /**
   * Проверка работоспособности API
   */
  async healthCheck(): Promise<{ status: string; message: string }> {
    const response = await this.client.get<UnknownRecord>("/v2/health");
    const status = response.data.status;

    return {
      status: typeof status === "string" ? status : "unknown",
      message: response.data.components ? "API operational" : "API status unknown",
    };
  }

  /**
   * Получение информации о модуле
   * Использует unified modules API: GET /v2/modules/{module_key}
   */
  async getModuleInfo(moduleKey: string): Promise<ParserCapabilities> {
    const response = await this.client.get<ParserCapabilities>(`/v2/modules/${moduleKey}`);
    return response.data;
  }

  // === УНИВЕРСАЛЬНЫЕ МЕТОДЫ UNIFIED MODULES API ===

  /**
   * Универсальный метод для асинхронного выполнения модуля через unified API.
   * Использует POST /v2/modules/{module_key}/execute_async
   *
   * @param moduleKey - Ключ модуля (например, "compliance.rus.fns_disqualified")
   * @param inputData - Входные данные для модуля
   * @param options - Опциональные настройки выполнения
   * @returns task_id для отслеживания задачи
   */
  /**
   * Справочник возможностей OSINT Tools: типы входа, источники по каждому и
   * категории. Спрашиваем у поставщика через наш бэкенд — держать копию
   * списка во фронте нельзя, он меняется на его стороне.
   */
  async getOsintCapabilities(): Promise<OsintCapabilities> {
    const response = await this.client.get<OsintCapabilities>("/v2/osint/capabilities");
    return response.data;
  }

  async executeModuleAsync(moduleKey: string, inputData: object, options: object = {}): Promise<string> {
    const response = await this.client.post<ModuleAsyncResponse>(`/v2/modules/${moduleKey}/execute_async`, {
      input_data: inputData,
      options,
    });
    // executor списал стоимость до постановки в очередь — баланс мог
    // измениться, оповестим UI (Sidebar и т.п.). Если модуль бесплатный
    // (TARIFF_NOT_FOUND) — это лишний refetch, ничем не плох.
    dispatchBalanceChanged();
    return response.data.task_id;
  }

  /**
   * Универсальный метод для синхронного выполнения модуля через unified API.
   * Использует POST /v2/modules/{module_key}/execute
   *
   * @param moduleKey - Ключ модуля (например, "compliance.rus.fns_disqualified")
   * @param inputData - Входные данные для модуля
   * @param options - Опциональные настройки выполнения
   * @returns Результат выполнения модуля
   */
  async executeModuleSync<T>(moduleKey: string, inputData: object, options: object = {}): Promise<T> {
    const response = await this.client.post<T>(`/v2/modules/${moduleKey}/execute`, { input_data: inputData, options });
    // см. комментарий в executeModuleAsync — оповещаем UI о возможном
    // изменении баланса.
    dispatchBalanceChanged();
    return this.normalizeComplianceModuleResponse(response.data);
  }

  /**
   * @deprecated Используйте executeModuleAsync вместо этого метода
   * Универсальный метод для асинхронного поиска в compliance парсерах (legacy)
   */
  async searchAsync(moduleKey: string, searchData: unknown): Promise<string> {
    // Преобразуем searchData в формат unified API
    const data = searchData as Record<string, unknown>;
    const inputData = (data.person_data || data) as object;
    const options = (data.module_options || {}) as object;
    return this.executeModuleAsync(moduleKey, inputData, options);
  }

  /**
   * @deprecated Используйте executeModuleAsync вместо этого метода
   * Специальный метод для отправки запроса с налоговым ID (для EFRSB)
   */
  async searchAsyncByTaxId(moduleKey: string, taxData: { tax_id: string }): Promise<string> {
    return this.executeModuleAsync(moduleKey, { tax_id: taxData.tax_id });
  }

  /**
   * Получение статуса задачи compliance
   */
  private async getComplianceTaskStatus(taskId: string): Promise<{ status: string; error?: string; result?: unknown }> {
    const response = await this.client.get<{ status: string; error?: string; result?: unknown }>(
      `/v2/task/${taskId}/status`,
    );
    return response.data;
  }

  /**
   * Получение результата задачи compliance
   */
  private async getComplianceTaskResult<TResponse>(taskId: string): Promise<TResponse> {
    const response = await this.client.get<TResponse>(`/v2/task/${taskId}/result`);
    // API возвращает CommonResponse/ComplianceApiResponse в зависимости от модуля
    return this.normalizeComplianceModuleResponse(response.data);
  }

  /**
   * Ожидание завершения compliance задачи
   */
  async waitForComplianceTask<TResponse>(taskId: string): Promise<TResponse> {
    const deadline = Date.now() + TASK_POLL_DEADLINE_MS;

    for (let attempt = 0; Date.now() < deadline; attempt++) {
      try {
        const status = await this.getComplianceTaskStatus(taskId);
        const statusValue = String(status.status).toUpperCase();

        if (statusValue === "SUCCESS" || statusValue === "COMPLETED") {
          // Результат приходит в формате модуля.
          return await this.getComplianceTaskResult<TResponse>(taskId);
        }
        if (statusValue === "FAILURE" || statusValue === "FAILED" || statusValue === "ERROR") {
          const errorMessage = (status as { error?: string }).error || "Неизвестная ошибка";
          throw new Error(`Задача завершилась с ошибкой: ${errorMessage}`);
        }
      } catch (error) {
        // Сетевую осечку пережидаем — задача на сервере продолжается. Отказ
        // самой задачи выброшен выше и наружу уходит как есть.
        if (error instanceof Error && error.message.startsWith("Задача завершилась с ошибкой")) {
          throw error;
        }
        if (Date.now() >= deadline) {
          throw error;
        }
      }

      await new Promise((resolve) => setTimeout(resolve, taskPollDelayMs(attempt)));
    }

    throw new Error("Таймаут ожидания завершения задачи");
  }

  // === VERIFICATION METHODS ===

  /**
   * Верификация ИНН по паспортным данным через ФНС России.
   *
   * Идёт через unified API (`/v2/modules/verification.fns_tax_id/execute`),
   * чтобы UnifiedTaskExecutor списал стоимость по тарифу
   * `api_verification_fns_tax_id`. Старый legacy-эндпоинт минует executor
   * и не проводит charge.
   */
  async verifyFnsTaxId(data: {
    last_name: string;
    first_name: string;
    middle_name?: string;
    birth_date: string;
    passport_series: string;
    passport_number: string;
  }): Promise<unknown> {
    return this.executeModuleSync<unknown>("verification.rus.fns_tax_id", {
      last_name: data.last_name,
      first_name: data.first_name,
      middle_name: data.middle_name || "",
      birth_date: data.birth_date,
      passport_series: data.passport_series,
      passport_number: data.passport_number,
    });
  }

  /**
   * Асинхронная верификация ИНН по паспортным данным через ФНС России.
   *
   * Идёт через unified API — биллинг списывается в executor до постановки
   * задачи в очередь.
   */
  async verifyFnsTaxIdAsync(data: {
    last_name: string;
    first_name: string;
    middle_name?: string;
    birth_date: string;
    passport_series: string;
    passport_number: string;
  }): Promise<string> {
    return this.executeModuleAsync("verification.rus.fns_tax_id", {
      last_name: data.last_name,
      first_name: data.first_name,
      middle_name: data.middle_name || "",
      birth_date: data.birth_date,
      passport_series: data.passport_series,
      passport_number: data.passport_number,
    });
  }

  /**
   * Проверка действительности российского паспорта.
   *
   * Идёт через unified API — биллинг по тарифу `api_verification_passport`.
   */
  async verifyPassport(data: {
    last_name: string;
    first_name: string;
    passport_series: string;
    passport_number: string;
    consent_personal_data_processing?: boolean;
  }): Promise<unknown> {
    return this.executeModuleSync<unknown>(
      "verification.rus.mvd_passport",
      {
        last_name: data.last_name,
        first_name: data.first_name,
        passport_series: data.passport_series,
        passport_number: data.passport_number,
      },
      {
        consent_personal_data_processing: data.consent_personal_data_processing ?? true,
      },
    );
  }

  /**
   * Асинхронная проверка действительности российского паспорта.
   *
   * Идёт через unified API — биллинг по тарифу `api_verification_passport`.
   */
  async verifyPassportAsync(data: {
    last_name: string;
    first_name: string;
    passport_series: string;
    passport_number: string;
    consent_personal_data_processing?: boolean;
  }): Promise<string> {
    return this.executeModuleAsync(
      "verification.rus.mvd_passport",
      {
        last_name: data.last_name,
        first_name: data.first_name,
        passport_series: data.passport_series,
        passport_number: data.passport_number,
      },
      {
        consent_personal_data_processing: data.consent_personal_data_processing ?? true,
      },
    );
  }

  // === MVD DRIVER LICENSE VERIFICATION ===

  /**
   * Проверка действительности водительского удостоверения (синхронно).
   *
   * Идёт через unified API — биллинг по тарифу `api_verification_driver`.
   * Адаптер ожидает `license_number` и `issue_date` (в legacy шейпе они
   * назывались `driver_license` / `driver_license_issue_date`; маппинг
   * выполняется здесь).
   */
  async verifyDriverLicense(data: {
    license_number: string;
    last_name: string;
    first_name: string;
    birth_date: string;
    middle_name?: string;
    issue_date?: string;
  }): Promise<unknown> {
    return this.executeModuleSync<unknown>("verification.rus.mvd_driver_license", {
      ...splitDriverLicense(data.license_number),
      issue_date: data.issue_date ?? "",
      last_name: data.last_name,
      first_name: data.first_name,
      middle_name: data.middle_name ?? "",
      birth_date: data.birth_date,
    });
  }

  /**
   * Асинхронная проверка действительности водительского удостоверения.
   *
   * Госуслуги (услуга 600445) требуют ФИО + дату рождения владельца — без
   * них backend ответит 400 «Для проверки ВУ через Госуслуги нужны
   * last_name, first_name и birth_date». Идёт через unified API — биллинг
   * по тарифу `api_verification_driver`.
   */
  async verifyDriverLicenseAsync(data: {
    license_number: string;
    last_name: string;
    first_name: string;
    birth_date: string;
    middle_name?: string;
    issue_date?: string;
  }): Promise<string> {
    return this.executeModuleAsync("verification.rus.mvd_driver_license", {
      ...splitDriverLicense(data.license_number),
      issue_date: data.issue_date ?? "",
      last_name: data.last_name,
      first_name: data.first_name,
      middle_name: data.middle_name ?? "",
      birth_date: data.birth_date,
    });
  }

  // === SFR SSN (SNILS) VERIFICATION ===

  /**
   * Проверка действительности СНИЛС (синхронно).
   *
   * Идёт через unified API — биллинг по тарифу `api_verification_sfr`.
   */
  async verifySfrSsn(data: {
    last_name: string;
    first_name: string;
    middle_name: string;
    birth_date: string;
    ssn: string;
  }): Promise<unknown> {
    return this.executeModuleSync<unknown>("verification.rus.sfr_ssn", {
      last_name: data.last_name,
      first_name: data.first_name,
      middle_name: data.middle_name,
      birth_date: data.birth_date,
      ssn: data.ssn,
    });
  }

  /**
   * Асинхронная проверка действительности СНИЛС.
   *
   * Идёт через unified API — биллинг по тарифу `api_verification_sfr`.
   */
  async verifySfrSsnAsync(data: {
    last_name: string;
    first_name: string;
    middle_name: string;
    birth_date: string;
    ssn: string;
  }): Promise<string> {
    return this.executeModuleAsync("verification.rus.sfr_ssn", {
      last_name: data.last_name,
      first_name: data.first_name,
      middle_name: data.middle_name,
      birth_date: data.birth_date,
      ssn: data.ssn,
    });
  }

  // === OBRNADZOR EDUCATION VERIFICATION ===

  /**
   * Проверка документа об образовании (синхронно).
   * Идёт через unified API `/v2/modules/verification.obrnadzor_education/execute`,
   * биллинг и refund на стороне UnifiedTaskExecutor.
   */
  async verifyEducation(data: {
    diploma_type: string;
    surname: string;
    series?: string;
    number_blank: string;
    issue_day?: string;
    issue_month?: string;
    issue_year: string;
    org_name: string;
    org_ogrn?: string;
    surname_lat?: string;
    register_number?: string;
  }): Promise<unknown> {
    return this.executeModuleSync<unknown>("verification.rus.obrnadzor_education", {
      diploma_type: data.diploma_type,
      surname: data.surname,
      series: data.series || "",
      number_blank: data.number_blank,
      issue_day: data.issue_day || "",
      issue_month: data.issue_month || "",
      issue_year: data.issue_year,
      org_name: data.org_name,
      org_ogrn: data.org_ogrn || "",
      surname_lat: data.surname_lat || "",
      register_number: data.register_number || "",
    });
  }

  /**
   * Асинхронная проверка документа об образовании.
   * Идёт через unified API `/v2/modules/verification.obrnadzor_education/execute_async`.
   */
  async verifyEducationAsync(data: {
    diploma_type: string;
    surname: string;
    series?: string;
    number_blank: string;
    issue_day?: string;
    issue_month?: string;
    issue_year: string;
    org_name: string;
    org_ogrn?: string;
    surname_lat?: string;
    register_number?: string;
  }): Promise<string> {
    return this.executeModuleAsync("verification.rus.obrnadzor_education", {
      diploma_type: data.diploma_type,
      surname: data.surname,
      series: data.series || "",
      number_blank: data.number_blank,
      issue_day: data.issue_day || "",
      issue_month: data.issue_month || "",
      issue_year: data.issue_year,
      org_name: data.org_name,
      org_ogrn: data.org_ogrn || "",
      surname_lat: data.surname_lat || "",
      register_number: data.register_number || "",
    });
  }

  // === REPORT METHODS ===

  /**
   * Создание комплексного отчёта
   */
  async createReport(
    personData: PersonData,
    options?: {
      priority?: number;
      timeout_seconds?: number;
      report_type?: "standard" | "full" | "ai";
      risk_matrix_id?: string;
      /**
       * Страны проверки (ISO alpha-2 средним сегментом ключа: "kaz", "rus",
       * "global"…). Пусто — все модули, применимые по данным субъекта;
       * со скоупом остаются модули выбранных стран и внестрановые
       * (санкционный скрининг нужен субъекту любой юрисдикции).
       */
      jurisdictions?: string[];
    },
  ): Promise<{
    job_id: string;
    status: string;
    total_tasks: number;
    modules: string[];
    data_completeness: number;
    stream_url: string;
  }> {
    // Трансформация данных для бэкенда
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const transformedData: Record<string, any> = { ...personData };

    // Объединяем серию и номер ВУ в одно поле driver_license
    if (personData.drivers_license_series || personData.drivers_license_number) {
      const series = (personData.drivers_license_series || "").replace(/\s/g, "");
      const number = (personData.drivers_license_number || "").replace(/\s/g, "");
      if (series || number) {
        transformedData.driver_license = `${series}${number}`;
      }
      // Маппинг даты выдачи ВУ
      if (personData.drivers_license_issue_date) {
        transformedData.driver_license_issue_date = personData.drivers_license_issue_date;
      }
      // Удаляем старые поля
      delete transformedData.drivers_license_series;
      delete transformedData.drivers_license_number;
      delete transformedData.drivers_license_issue_date;
    }

    // Преобразуем контактные данные в массивы для валидации
    if (personData.phone_number) {
      transformedData.phones = [personData.phone_number];
      delete transformedData.phone_number;
    }
    if (personData.email) {
      transformedData.emails = [personData.email];
      delete transformedData.email;
    }
    if (personData.registration_address) {
      transformedData.addresses = [personData.registration_address];
      delete transformedData.registration_address;
    }

    const { risk_matrix_id, ...checkOptions } = options || {};
    const response = await this.client.post("/v2/check/person/", {
      person_data: transformedData,
      options: checkOptions,
      risk_matrix_id: risk_matrix_id,
    });
    return response.data;
  }

  /**
   * Получение итогового отчёта UnifiedPersonReport.
   *
   * @param jobId Идентификатор job.
   * @param allowPartial Если true — backend вернёт промежуточный снапшот
   *   in-progress job (с секцией `_partial`) вместо 409. Используется при
   *   восстановлении состояния после обрыва SSE и при входе из истории.
   */
  async getReportResult(jobId: string, allowPartial: boolean = false): Promise<unknown> {
    const params = allowPartial ? { allow_partial: true } : undefined;
    const response = await this.client.get(`/v2/report/complex/${jobId}/result`, { params });

    // Эндпоинт отдаёт UnifiedPersonReport в виде `{ person: PersonDE, report: meta }`,
    // а фронт (setReport / extractTasksFromReport и весь рендеринг отчёта) работает
    // с полями PersonDE на верхнем уровне (compliance, basic_information и т.д.).
    // Разворачиваем `person`, перенося маркер `_partial`. Без этого при входе из
    // истории `result.compliance` оказывается undefined, preview модулей не
    // извлекается и блоки данных не отображаются (хотя статус «найдено» виден).
    // Фолбэк на сырой ответ — для обратной совместимости со старым форматом.
    const data = response.data as Record<string, unknown> | null;
    if (data && typeof data === "object" && "person" in data && data.person) {
      const person = data.person as Record<string, unknown>;
      return "_partial" in data ? { ...person, _partial: data._partial } : person;
    }
    return data;
  }

  /**
   * Получение URL для SSE подключения
   */
  getReportStreamUrl(jobId: string): string {
    return `${this.baseURL}/v2/check/person/${jobId}/stream`;
  }

  // === LEGAL ENTITY COMPLEX REPORT (SSE-архитектура) ===

  /**
   * Запустить комплексный отчёт по ЮЛ.
   */
  async createLegalCheck(payload: { inn?: string; ogrn?: string }): Promise<{
    job_id: string;
    status: string;
    total_stages: number;
    stream_url: string;
    status_url: string;
  }> {
    const response = await this.client.post("/v2/check/legal/", payload);
    return response.data;
  }

  /**
   * Snapshot текущего состояния job для ЮЛ.
   */
  async getLegalCheckSnapshot(jobId: string): Promise<unknown> {
    const response = await this.client.get(`/v2/check/legal/${jobId}`);
    return response.data;
  }

  // === ФИЗЮР: связи списка физлиц с компаниями (выгрузка в Excel) ===

  /**
   * Поставить выгрузку: файл со списком ИНН физлиц (CSV/TXT/XLS/XLSX/DOC/DOCX).
   */
  async createFizurJob(file: File): Promise<FizurJob> {
    const form = new FormData();
    form.append("file", file);
    const response = await this.client.post("/v2/fizur/jobs", form, { timeout: 120000 });
    return response.data;
  }

  /**
   * Состояние одной выгрузки.
   */
  async getFizurJob(jobId: string): Promise<FizurJob> {
    const response = await this.client.get(`/v2/fizur/jobs/${jobId}`);
    return response.data;
  }

  /**
   * Скачать готовый xlsx.
   */
  async downloadFizurJob(jobId: string): Promise<Blob> {
    const response = await this.client.get(`/v2/fizur/jobs/${jobId}/download`, {
      responseType: "blob",
      timeout: 120000,
    });
    return response.data;
  }

  /**
   * URL для SSE-стрима по ЮЛ.
   */
  getLegalReportStreamUrl(jobId: string): string {
    return `${this.baseURL}/v2/check/legal/${jobId}/stream`;
  }

  // === DADATA API ПОДСКАЗКИ ===

  /**
   * Интерфейс для подсказки организации из Dadata
   */

  /**
   * Поиск организаций по названию через Dadata API
   * @param query - Поисковый запрос (частичное название организации)
   * @param count - Количество результатов (по умолчанию 10, максимум 20)
   * @returns Массив подсказок организаций
   */
  async suggestOrganizations(
    query: string,
    count: number = 10,
  ): Promise<
    Array<{
      value: string;
      unrestricted_value: string;
      data: {
        inn: string | null;
        kpp: string | null;
        ogrn: string | null;
        type: string | null;
        name: {
          full: string | null;
          short: string | null;
          full_with_opf: string | null;
          short_with_opf: string | null;
        } | null;
        address: {
          value: string | null;
        } | null;
        state: {
          status: string | null;
        } | null;
      };
    }>
  > {
    if (!query || query.length < 2) {
      return [];
    }

    try {
      const response = await this.client.get("/v2/registration/suggest-org", {
        params: { query, count: Math.min(count, 20) },
        timeout: 5000,
      });

      if (response.data.success) {
        return response.data.suggestions || [];
      }
      return [];
    } catch (error) {
      console.error("Ошибка подсказок организаций:", error);
      return [];
    }
  }

  /**
   * Получить подсказки банков по БИК или названию.
   * @param query - БИК (9 цифр) или начало названия банка
   * @param count - Количество результатов
   * @returns Массив подсказок банков
   */
  async suggestBanks(
    query: string,
    count: number = 5,
  ): Promise<
    Array<{
      value: string;
      bic: string;
      correspondent_account: string;
      city: string;
      name: string;
    }>
  > {
    if (!query || query.length < 2) {
      return [];
    }

    try {
      const response = await this.client.get<{
        success: boolean;
        suggestions: Array<{
          value: string;
          bic: string;
          correspondent_account: string;
          city: string;
          name: string;
        }>;
        error?: string;
      }>("/v2/registration/suggest-bank", {
        params: { query, count },
      });

      if (response.data.success) {
        return response.data.suggestions || [];
      }
      return [];
    } catch (error) {
      console.error("Ошибка получения подсказок банков:", error);
      return [];
    }
  }

  /**
   * Получить данные руководителя компании по ИНН (для автозаполнения подписанта).
   */
  async getCompanyManager(inn: string): Promise<{
    success: boolean;
    manager?: { full_name: string; position: string };
    error?: string;
  }> {
    const response = await this.client.get(`/v2/registration/company-manager/${inn}`);
    return response.data;
  }

  /**
   * Подать заявку на регистрацию организации.
   */
  async submitRegistration(data: Record<string, unknown>): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    application_id?: number;
  }> {
    const response = await this.client.post("/v2/registration/submit", data);
    return response.data;
  }

  // === LEGAL ENTITY / BUSINESS METHODS ===

  /**
   * Получить комплексный отчёт по юридическому лицу (по ИНН)
   */
  async getLegalEntityReport(inn: string, options: CompanyReportOptions = {}): Promise<CompanyReportResponse> {
    const response = await this.client.post<CommonResponse<CompanyReportResponse | null>>(
      `/v2/modules/${BUSINESS_MODULES.LEGAL_REPORT}/execute`,
      { input_data: { inn: inn.trim() }, options },
      // The Go module has a 180-second deadline; allow its error response to arrive.
      { timeout: 190000 },
    );
    dispatchBalanceChanged();
    const envelope = response.data;
    const data = envelope?.data;
    if (
      !envelope?.success ||
      envelope.status !== "COMPLETED" ||
      !data?.success ||
      !this.isObject(data.report) ||
      Array.isArray(data.report)
    ) {
      throw new ApiError(envelope?.error || data?.error || "Не удалось получить отчёт компании");
    }
    return {
      ...data,
      // The module data has a placeholder duration; use the executor's measurement.
      processing_time_ms: envelope.metadata?.processing_time_ms ?? data.processing_time_ms,
      timestamp: envelope.metadata?.completed_at || data.timestamp || envelope.metadata?.timestamp,
      error: data.error ?? null,
    };
  }

  // === ADMIN CLIENT MANAGEMENT ===

  /**
   * Получить список клиентов-организаций
   * @param params - Параметры фильтрации и пагинации
   * @returns Список клиентов с общим количеством
   */
  async getClients(params: GetClientsParams = {}): Promise<ClientsListResponse> {
    const queryParams = new URLSearchParams();

    if (params.limit !== undefined) {
      queryParams.append("limit", String(params.limit));
    }
    if (params.offset !== undefined) {
      queryParams.append("offset", String(params.offset));
    }
    if (params.status_filter) {
      queryParams.append("status_filter", params.status_filter);
    }
    if (params.search_query) {
      queryParams.append("search_query", params.search_query);
    }

    const queryString = queryParams.toString();
    const url = `/v2/admin/clients${queryString ? `?${queryString}` : ""}`;

    const response = await this.client.get<ClientsListResponse>(url);
    return response.data;
  }

  /**
   * Создать организацию вручную (администратор)
   */
  async createOrganization(data: CreateOrganizationRequest): Promise<CreateOrganizationResponse> {
    const response = await this.client.post<CreateOrganizationResponse>("/v2/admin/clients", data);
    return response.data;
  }

  /**
   * Удалить организацию (администратор)
   */
  async deleteOrganization(
    organizationId: number,
  ): Promise<{ success: boolean; organization_id: number; inn: string; name: string }> {
    const response = await this.client.delete<{
      success: boolean;
      organization_id: number;
      inn: string;
      name: string;
    }>(`/v2/admin/clients/${organizationId}`);
    return response.data;
  }

  /**
   * Обновить данные организации (администратор)
   */
  async updateOrganization(
    organizationId: number,
    data: UpdateOrganizationRequest,
  ): Promise<{ success: boolean; organization_id: number; updated_fields: string[] }> {
    const response = await this.client.patch<{
      success: boolean;
      organization_id: number;
      updated_fields: string[];
    }>(`/v2/admin/clients/${organizationId}`, data);
    return response.data;
  }

  /**
   * Переключить модель управления тарифом организации (администратор).
   */
  async setClientManagementType(
    organizationId: number,
    managementType: ManagementType,
  ): Promise<{ success: boolean; organization_id: number; management_type: ManagementType }> {
    const response = await this.client.patch<{
      success: boolean;
      organization_id: number;
      management_type: ManagementType;
    }>(`/v2/admin/clients/${organizationId}/management-type`, {
      management_type: managementType,
    });
    return response.data;
  }

  /**
   * Получить план авто-повторяющегося гранта организации (администратор).
   */
  async getClientExternalPlan(organizationId: number): Promise<ExternalPlanResponse> {
    const response = await this.client.get<ExternalPlanResponse>(`/v2/admin/clients/${organizationId}/external-plan`);
    return response.data;
  }

  /**
   * Сохранить план авто-повторяющегося гранта организации (администратор).
   */
  async updateClientExternalPlan(
    organizationId: number,
    data: UpdateExternalPlanRequest,
  ): Promise<ExternalPlanResponse> {
    const response = await this.client.put<ExternalPlanResponse>(
      `/v2/admin/clients/${organizationId}/external-plan`,
      data,
    );
    return response.data;
  }

  /**
   * Демо-статус текущего пользователя: баннер и бейдж кабинета.
   */
  async getDemoStatus(): Promise<DemoStatus> {
    const response = await this.client.get<DemoStatus>("/v2/organization/demo");
    return response.data;
  }

  /**
   * Демо-доступы потенциальных клиентов (администратор).
   */
  async listDemoAccounts(): Promise<DemoAccount[]> {
    const response = await this.client.get<{ total: number; items: DemoAccount[] }>("/v2/admin/demo-accounts");
    return response.data.items;
  }

  /**
   * Выдать демо-доступ: отдельная демо-организация и письмо со ссылкой входа.
   */
  async createDemoAccount(data: CreateDemoAccountRequest): Promise<DemoAccount> {
    const response = await this.client.post<DemoAccount>("/v2/admin/demo-accounts", data);
    return response.data;
  }

  /**
   * Продлить демо: срок прибавляется к текущему окончанию.
   */
  async extendDemoAccount(organizationId: number, data: ExtendDemoRequest): Promise<DemoAccess> {
    const response = await this.client.put<DemoAccess>(`/v2/admin/demo-accounts/${organizationId}`, data);
    return response.data;
  }

  /**
   * Удалить демо-доступ: демо-организация удаляется целиком.
   */
  async deleteDemoAccount(organizationId: number): Promise<void> {
    await this.client.delete(`/v2/admin/demo-accounts/${organizationId}`);
  }

  /**
   * Разовый административный грант организации (администратор).
   */
  async grantClientSubscription(
    organizationId: number,
    data: GrantSubscriptionRequest,
  ): Promise<GrantSubscriptionResponse> {
    const response = await this.client.post<GrantSubscriptionResponse>(
      `/v2/admin/clients/${organizationId}/grant`,
      data,
    );
    return response.data;
  }

  // === ADMIN STAFF MANAGEMENT ===

  /**
   * Получить список сотрудников платформы
   * @param params - Параметры фильтрации и пагинации
   * @returns Список сотрудников с общим количеством
   */
  async getStaff(params: GetStaffParams = {}): Promise<StaffListResponse> {
    const queryParams = new URLSearchParams();

    if (params.limit !== undefined) {
      queryParams.append("limit", String(params.limit));
    }
    if (params.offset !== undefined) {
      queryParams.append("offset", String(params.offset));
    }
    if (params.role_filter) {
      queryParams.append("role_filter", params.role_filter);
    }
    if (params.status_filter) {
      queryParams.append("status_filter", params.status_filter);
    }

    const queryString = queryParams.toString();
    const url = `/v2/admin/staff${queryString ? `?${queryString}` : ""}`;

    const response = await this.client.get<StaffListResponse>(url);
    return response.data;
  }

  /**
   * Получить сотрудника по ID
   */
  async getStaffMember(userId: number): Promise<StaffMember> {
    const response = await this.client.get<StaffMember>(`/v2/admin/staff/${userId}`);
    return response.data;
  }

  /**
   * Создать сотрудника платформы
   */
  async createStaff(data: CreateStaffRequest): Promise<CreateStaffResponse> {
    const response = await this.client.post<CreateStaffResponse>("/v2/admin/staff", data);
    return response.data;
  }

  /**
   * Обновить сотрудника платформы
   */
  async updateStaff(userId: number, data: UpdateStaffRequest): Promise<StaffOnboardingResponse> {
    const response = await this.client.patch<StaffOnboardingResponse>(`/v2/admin/staff/${userId}`, data);
    return response.data;
  }

  /**
   * Удалить сотрудника платформы
   */
  async deleteStaff(userId: number): Promise<{ success: boolean; user_id: number }> {
    const response = await this.client.delete<{ success: boolean; user_id: number }>(`/v2/admin/staff/${userId}`);
    return response.data;
  }

  /**
   * Переиздать доступ сотрудника (новая onboarding-ссылка)
   */
  async resetStaffPassword(userId: number): Promise<ResetPasswordResponse> {
    const response = await this.client.post<ResetPasswordResponse>(`/v2/admin/staff/${userId}/reset-password`);
    return response.data;
  }

  // === BILLING V3 ADMIN ===

  /**
   * Получить все сервисные пакеты (для админа)
   */
  async getServicePackages(includeInactive: boolean = true): Promise<ServicePackageListResponse> {
    const response = await this.client.get<ServicePackageListResponse>("/v2/admin/billing/packages", {
      params: { include_inactive: includeInactive },
    });
    return response.data;
  }

  /**
   * Создать сервисный пакет
   */
  async createServicePackage(data: CreateServicePackageRequest): Promise<ServicePackage> {
    const response = await this.client.post<ServicePackage>("/v2/admin/billing/packages", data);
    return response.data;
  }

  /**
   * Обновить сервисный пакет
   */
  async updateServicePackage(packageId: number, data: UpdateServicePackageRequest): Promise<ServicePackage> {
    const response = await this.client.patch<ServicePackage>(`/v2/admin/billing/packages/${packageId}`, data);
    return response.data;
  }

  /**
   * Деактивировать сервисный пакет
   */
  async deleteServicePackage(packageId: number): Promise<void> {
    await this.client.delete(`/v2/admin/billing/packages/${packageId}`);
  }

  /**
   * Получить список зарегистрированных модулей (validation/verification/compliance/osint/business)
   */
  async getRegisteredModules(category?: string, enabledOnly: boolean = true): Promise<RegisteredModulesListResponse> {
    const params: Record<string, string | boolean> = { enabled_only: enabledOnly };
    if (category) {
      params.category = category;
    }
    const response = await this.client.get<RegisteredModulesListResponse>("/v2/modules", { params });
    return response.data;
  }

  /**
   * Получить все тарифы (для админа)
   */
  async getTariffs(channel?: TariffChannel, includeInactive: boolean = true): Promise<TariffListResponse> {
    const response = await this.client.get<TariffListResponse>("/v2/admin/billing/tariffs", {
      params: { channel, include_inactive: includeInactive },
    });
    return response.data;
  }

  /**
   * Создать тариф
   */
  async createTariff(data: CreateTariffRequest): Promise<BillingTariff> {
    const response = await this.client.post<BillingTariff>("/v2/admin/billing/tariffs", data);
    return response.data;
  }

  /**
   * Обновить тариф
   */
  async updateTariff(tariffId: number, data: UpdateTariffRequest): Promise<BillingTariff> {
    const response = await this.client.patch<BillingTariff>(`/v2/admin/billing/tariffs/${tariffId}`, data);
    return response.data;
  }

  /**
   * Деактивировать тариф
   */
  async deleteTariff(tariffId: number): Promise<void> {
    await this.client.delete(`/v2/admin/billing/tariffs/${tariffId}`);
  }

  // === BILLING V3 CLIENT ===

  /**
   * Получить баланс организации
   */
  async getBillingBalance(): Promise<BillingBalanceResponse> {
    const response = await this.client.get<BillingBalanceResponse>("/v2/billing/balance");
    return response.data;
  }

  /**
   * Индивидуальный лимит текущего пользователя (его cap/reserve).
   */
  async getMySpendingLimit(): Promise<MySpendingLimitResponse> {
    const response = await this.client.get<MySpendingLimitResponse>("/v2/billing/my-limits");
    return response.data;
  }

  /**
   * Сводка резервируемого баланса организации (для админ-баннера).
   * Только для администраторов организации/платформы.
   */
  async getOrgReservableInfo(): Promise<OrgReservableInfoResponse> {
    const response = await this.client.get<OrgReservableInfoResponse>("/v2/billing/reservable-info");
    return response.data;
  }

  /**
   * Получить список сервисных пакетов (публичный)
   */
  async getBillingPackages(): Promise<ClientPackagesResponse> {
    const response = await this.client.get<ClientPackagesResponse>("/v2/billing/packages");
    return response.data;
  }

  /**
   * Предварительная сверка лимитов при выборе пакета.
   * Возвращает баланс будущего пакета, текущую сумму гарантий и
   * пользователей с резервами. При has_conflict=true фронт должен
   * показать экран «Сверка лимитов».
   */
  async previewCheckout(servicePackageId: number): Promise<PreviewCheckoutResponse> {
    const response = await this.client.post<PreviewCheckoutResponse>("/v2/billing/checkout/preview", {
      service_package_id: servicePackageId,
    });
    return response.data;
  }

  /**
   * Создать инвойс на покупку пакета (с возможной стратегией сверки).
   */
  async createCheckout(
    servicePackageId: number,
    resolveStrategy?: Record<string, unknown> | null,
  ): Promise<CheckoutResponse> {
    const response = await this.client.post<CheckoutResponse>("/v2/billing/checkout", {
      service_package_id: servicePackageId,
      resolve_strategy: resolveStrategy ?? null,
    });
    return response.data;
  }

  /**
   * Получить список тарифов (публичный)
   */
  async getBillingTariffs(channel?: TariffChannel): Promise<ClientTariffsResponse> {
    const response = await this.client.get<ClientTariffsResponse>("/v2/billing/tariffs", {
      params: channel ? { channel } : undefined,
    });
    return response.data;
  }

  /**
   * Получить историю транзакций
   */
  async getBillingTransactions(
    page: number = 1,
    pageSize: number = 20,
    transactionType?: TransactionType,
  ): Promise<BillingTransactionsResponse> {
    const response = await this.client.get<BillingTransactionsResponse>("/v2/billing/transactions", {
      params: { page, page_size: pageSize, transaction_type: transactionType },
    });
    return response.data;
  }

  /**
   * Создать счёт на оплату
   */
  async createBillingInvoice(data: CreateBillingInvoiceRequest): Promise<CreateBillingInvoiceResponse> {
    const response = await this.client.post<CreateBillingInvoiceResponse>("/v2/billing/invoices", data);
    return response.data;
  }

  /**
   * Получить список моих счетов (клиент)
   */
  async getMyInvoices(): Promise<ClientInvoiceListResponse> {
    const response = await this.client.get<ClientInvoiceListResponse>("/v2/billing/invoices");
    return response.data;
  }

  /**
   * Скачать PDF-счёт (blob)
   */
  async downloadInvoicePdf(invoiceId: number): Promise<Blob> {
    const response = await this.client.get(`/v2/billing/invoices/${invoiceId}/download-pdf`, {
      responseType: "blob",
    });
    return response.data;
  }

  /**
   * Скачать УПД для счёта (клиент)
   */
  async downloadInvoiceUpd(invoiceId: number): Promise<Blob> {
    const response = await this.client.get(`/v2/billing/invoices/${invoiceId}/download-upd`, {
      responseType: "blob",
    });
    return response.data;
  }

  /**
   * Загрузить платёжное поручение (клиент)
   */
  async uploadPaymentConfirmation(invoiceId: number, file: File): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);
    await this.client.post(`/v2/billing/invoices/${invoiceId}/upload-confirmation`, formData);
  }

  /**
   * Получить настройку авто-продления для текущей организации
   */
  async getAutoRenewal(): Promise<AutoRenewalConfigResponse> {
    const response = await this.client.get<AutoRenewalConfigResponse>("/v2/billing/auto-renewal");
    return response.data;
  }

  /**
   * Сохранить настройку авто-продления для текущей организации
   */
  async updateAutoRenewal(data: UpdateAutoRenewalRequest): Promise<AutoRenewalConfigResponse> {
    const response = await this.client.put<AutoRenewalConfigResponse>("/v2/billing/auto-renewal", data);
    return response.data;
  }

  /**
   * Очередь сервисных пакетов организации: активная подписка, queued, опционально закрытые.
   */
  async getPackageSubscriptions(includeClosed = false): Promise<PackageSubscriptionListResponse> {
    const response = await this.client.get<PackageSubscriptionListResponse>("/v2/billing/subscriptions", {
      params: { include_closed: includeClosed },
    });
    return response.data;
  }

  /**
   * Досрочно переключить активную подписку на queued из очереди.
   * Сервер вернёт 400 для TARGET_NOT_LARGER, 404 для TARGET_NOT_FOUND и т.д.
   */
  async activatePackageSubscription(subscriptionId: number): Promise<SwitchPackageSubscriptionResponse> {
    const response = await this.client.post<SwitchPackageSubscriptionResponse>(
      `/v2/billing/subscriptions/${subscriptionId}/activate`,
    );
    return response.data;
  }

  // === SERVICE ADDONS (внешние сервисы, например VED) ===

  /**
   * Справочник доступных аддонов; опциональный фильтр по группе (например, 'VED').
   */
  async listAddons(groupKey?: string): Promise<AddonsResponse> {
    const response = await this.client.get<AddonsResponse>("/v2/billing/addons", {
      params: groupKey ? { group_key: groupKey } : undefined,
    });
    return response.data;
  }

  /**
   * Аддон-подписки организации: active, queued и (по запросу) последние expired.
   */
  async listMyAddonSubscriptions(
    opts: {
      groupKey?: string;
      includeExpired?: boolean;
    } = {},
  ): Promise<AddonSubscriptionsResponse> {
    const params: Record<string, string | boolean> = {};
    if (opts.groupKey) params.group_key = opts.groupKey;
    if (opts.includeExpired) params.include_expired = true;
    const response = await this.client.get<AddonSubscriptionsResponse>("/v2/billing/addons/my", { params });
    return response.data;
  }

  /**
   * Создаёт счёт на покупку аддона. Подписка активируется после ручного
   * подтверждения оплаты администратором.
   */
  async purchaseAddon(code: string, opts: { dueDate?: string } = {}): Promise<PurchaseAddonResponse> {
    const response = await this.client.post<PurchaseAddonResponse>(
      `/v2/billing/addons/${encodeURIComponent(code)}/purchase`,
      opts.dueDate ? { due_date: opts.dueDate } : {},
    );
    return response.data;
  }

  /**
   * Очередь пакетов произвольной организации (admin).
   * Требует прав администратора на сервере.
   */
  async getAdminPackageSubscriptions(
    organizationId: number,
    opts: { includeClosed?: boolean; closedLimit?: number } = {},
  ): Promise<PackageSubscriptionListResponse> {
    const response = await this.client.get<PackageSubscriptionListResponse>(
      `/v2/admin/billing/organizations/${organizationId}/subscriptions`,
      {
        params: {
          include_closed: opts.includeClosed ?? false,
          closed_limit: opts.closedLimit ?? 20,
        },
      },
    );
    return response.data;
  }

  // === IN-APP NOTIFICATIONS ===

  /** Лента уведомлений (свои + организации) с признаком прочтения. */
  async listNotifications(opts?: {
    onlyUnread?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<NotificationListResponse> {
    const response = await this.client.get<NotificationListResponse>("/v2/notifications", {
      params: {
        only_unread: opts?.onlyUnread ?? false,
        limit: opts?.limit ?? 20,
        offset: opts?.offset ?? 0,
      },
    });
    return response.data;
  }

  /** Счётчик непрочитанных для иконки-колокольчика. */
  async getNotificationsUnreadCount(): Promise<NotificationUnreadCountResponse> {
    const response = await this.client.get<NotificationUnreadCountResponse>("/v2/notifications/unread-count");
    return response.data;
  }

  /** Отметить одно уведомление прочитанным. */
  async markNotificationRead(notificationId: number): Promise<void> {
    await this.client.post(`/v2/notifications/${notificationId}/read`);
  }

  /** Отметить все видимые уведомления прочитанными. */
  async markAllNotificationsRead(): Promise<NotificationMarkAllReadResponse> {
    const response = await this.client.post<NotificationMarkAllReadResponse>("/v2/notifications/mark-all-read");
    return response.data;
  }

  /** Полная матрица настроек доставки (kind × extra_channels). */
  async getNotificationPreferences(): Promise<NotificationPreferencesResponse> {
    const response = await this.client.get<NotificationPreferencesResponse>("/v2/notifications/preferences");
    return response.data;
  }

  /** Обновить настройку доставки для одного kind. */
  async updateNotificationPreference(data: UpdateNotificationPreferenceRequest): Promise<NotificationPreferenceItem> {
    const response = await this.client.put<NotificationPreferenceItem>("/v2/notifications/preferences", data);
    return response.data;
  }

  // === BILLING V3 ADMIN INVOICES ===

  /**
   * Получить все счета (админ)
   */
  async getAdminInvoices(
    statusFilter?: InvoiceStatus,
    page: number = 1,
    pageSize: number = 50,
  ): Promise<AdminInvoiceListResponse> {
    const response = await this.client.get<AdminInvoiceListResponse>("/v2/admin/billing/invoices", {
      params: { status: statusFilter, page, page_size: pageSize },
    });
    return response.data;
  }

  /**
   * Загрузить PDF-счёт (админ).
   *
   * Номер счёта (`invoiceNumber`) задаёт администратор — он перезаписывает
   * placeholder, выданный при создании черновика, и используется в письме
   * клиенту и в карточке счёта.
   */
  async uploadInvoicePdf(invoiceId: number, file: File, invoiceNumber: string): Promise<AdminInvoiceItem> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("invoice_number", invoiceNumber);
    const response = await this.client.post<AdminInvoiceItem>(
      `/v2/admin/billing/invoices/${invoiceId}/upload-pdf`,
      formData,
    );
    return response.data;
  }

  /**
   * Подтвердить оплату счёта (админ)
   */
  async confirmInvoicePayment(invoiceId: number): Promise<AdminInvoiceItem> {
    const response = await this.client.post<AdminInvoiceItem>(`/v2/admin/billing/invoices/${invoiceId}/confirm`, {});
    return response.data;
  }

  /**
   * Отклонить счёт (админ)
   */
  async rejectInvoice(invoiceId: number): Promise<AdminInvoiceItem> {
    const response = await this.client.post<AdminInvoiceItem>(`/v2/admin/billing/invoices/${invoiceId}/reject`);
    return response.data;
  }

  /**
   * Загрузить УПД (админ)
   */
  async uploadUpdFile(invoiceId: number, file: File): Promise<AdminInvoiceItem> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await this.client.post<AdminInvoiceItem>(
      `/v2/admin/billing/invoices/${invoiceId}/upload-upd`,
      formData,
    );
    return response.data;
  }

  /**
   * Скачать PDF-счёт (админ)
   */
  async downloadAdminInvoicePdf(invoiceId: number): Promise<Blob> {
    const response = await this.client.get(`/v2/admin/billing/invoices/${invoiceId}/download-pdf`, {
      responseType: "blob",
    });
    return response.data;
  }

  /**
   * Скачать платёжное поручение, загруженное клиентом (админ)
   */
  async downloadAdminPaymentConfirmation(invoiceId: number): Promise<Blob> {
    const response = await this.client.get(`/v2/admin/billing/invoices/${invoiceId}/download-confirmation`, {
      responseType: "blob",
    });
    return response.data;
  }

  /**
   * Скачать УПД (админ)
   */
  async downloadAdminInvoiceUpd(invoiceId: number): Promise<Blob> {
    const response = await this.client.get(`/v2/admin/billing/invoices/${invoiceId}/download-upd`, {
      responseType: "blob",
    });
    return response.data;
  }

  // === КАДАСТРОВЫЕ ДАННЫЕ ===

  /**
   * Информация о кадастровом объекте
   */
  async getCadastralInfo(cadastralNumber: string): Promise<CadastralInfo | null> {
    try {
      const response = await this.client.get<CadastralInfo>(
        `/v2/property/cadastral/${encodeURIComponent(cadastralNumber)}`,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      console.error("Ошибка получения кадастровых данных:", error);
      return null;
    }
  }

  // === OSINT ОБОГАЩЕНИЕ ===

  private osintLog = createLogger("ApiService:OSINT");

  getOsintStreamUrl(taskId: string): string {
    return `${this.baseURL}/v2/osint/stream/${taskId}`;
  }

  /**
   * Получить статус OSINT обогащения контакта
   *
   * @param taskId - ID задачи OSINT обогащения
   * @returns Статус задачи с прогрессом
   */
  async getOsintStatus(taskId: string): Promise<OsintTaskStatusResponse> {
    this.osintLog.debug("getOsintStatus called", { taskId });
    try {
      const response = await this.client.get(`/v2/osint/status/${taskId}`);
      this.osintLog.info("getOsintStatus response", {
        taskId,
        status: response.data.status,
        progress: response.data.progress,
        completedModules: response.data.completed_modules,
        totalModules: response.data.total_modules,
      });
      // API отдает статус строчными буквами, интерфейс сравнивает с заглавными.
      return { ...response.data, status: String(response.data.status).toUpperCase() };
    } catch (error) {
      this.osintLog.error("getOsintStatus error", { taskId, error: error instanceof Error ? error.message : error });
      throw error;
    }
  }

  /**
   * Получить результаты OSINT обогащения контакта
   *
   * @param taskId - ID задачи OSINT обогащения
   * @returns Полные результаты обогащения
   */
  async getOsintResult(taskId: string): Promise<OsintEnrichmentResponse> {
    this.osintLog.debug("getOsintResult called", { taskId });
    try {
      const response = await this.client.get(`/v2/osint/result/${taskId}`);
      this.osintLog.info("getOsintResult response", {
        taskId,
        contactType: response.data.contact_type,
        contactValue: response.data.contact_value,
        modulesCount: response.data.modules?.length,
        foundModulesCount: response.data.modules?.filter((m: { found: boolean }) => m.found).length,
      });
      return response.data;
    } catch (error) {
      this.osintLog.error("getOsintResult error", { taskId, error: error instanceof Error ? error.message : error });
      throw error;
    }
  }

  // === OSINT АГРЕГАТ НА ОТЧЁТ (batch-per-report) ===

  /**
   * Получить агрегатный статус OSINT обогащения для отчёта.
   * Используется как REST-fallback к SSE и при открытии готовых отчётов.
   *
   * @param reportId - ID отчёта (job_id)
   * @returns Статус всех OSINT задач отчёта
   */
  async getOsintReportStatus(reportId: string): Promise<OsintReportStatus | null> {
    this.osintLog.debug("getOsintReportStatus called", { reportId });
    try {
      const response = await this.client.get<OsintReportStatus>(`/v2/osint/report/${reportId}/status`);
      return response.data;
    } catch (error) {
      if (error instanceof ApiError && error.httpStatus === 404) {
        return null;
      }
      this.osintLog.error("getOsintReportStatus error", {
        reportId,
        error: error instanceof Error ? error.message : error,
      });
      throw error;
    }
  }

  /**
   * Получить агрегатные результаты OSINT обогащения для отчёта.
   *
   * @param reportId - ID отчёта (job_id)
   * @returns Lookups по контактам или null, если данных нет
   */
  async getOsintReportResults(reportId: string): Promise<OsintReportResults | null> {
    this.osintLog.debug("getOsintReportResults called", { reportId });
    try {
      const response = await this.client.get<OsintReportResults>(`/v2/osint/report/${reportId}/results`);
      return response.data;
    } catch (error) {
      if (error instanceof ApiError && error.httpStatus === 404) {
        return null;
      }
      this.osintLog.error("getOsintReportResults error", {
        reportId,
        error: error instanceof Error ? error.message : error,
      });
      throw error;
    }
  }

  /**
   * Быстрая проверка наличия OSINT данных для отчёта.
   *
   * @param reportId - ID отчёта (job_id)
   */
  async getOsintReportExists(reportId: string): Promise<OsintReportExists> {
    const response = await this.client.get<OsintReportExists>(`/v2/osint/report/${reportId}/exists`);
    return response.data;
  }

  // === ADMIN: ЗАЯВКИ НА РЕГИСТРАЦИЮ ===

  /**
   * Получить список заявок на регистрацию
   */
  async getApplications(params: { limit?: number; offset?: number; status?: string }): Promise<{
    success: boolean;
    items: Array<{
      id: number;
      organization_id: number;
      organization_name: string;
      organization_inn: string;
      entity_type: string;
      status: string;
      contact_email: string;
      contact_phone?: string;
      assigned_manager_id?: number;
      assigned_manager_name?: string;
      rejection_reason?: string;
      created_at: string;
      updated_at: string;
      edo_invited_at?: string;
      contract_signed_at?: string;
      activated_at?: string;
    }>;
    total: number;
    error?: string;
  }> {
    const response = await this.client.get("/v2/admin/applications", { params });
    return response.data;
  }

  /**
   * Изменить статус заявки
   */
  async updateApplicationStatus(
    applicationId: number,
    data: {
      status: string;
      comment?: string;
      rejection_reason?: string;
    },
  ): Promise<{
    success: boolean;
    application?: {
      id: number;
      status: string;
      updated_at: string;
    };
    error?: string;
  }> {
    const response = await this.client.post(`/v2/admin/applications/${applicationId}/status`, data);
    return response.data;
  }

  /**
   * Активировать клиента (создать пользователя и отправить письмо)
   */
  async activateClient(applicationId: number): Promise<{
    success: boolean;
    user_id?: number;
    organization_id?: number;
    message?: string;
    error?: string;
  }> {
    const response = await this.client.post(`/v2/admin/applications/${applicationId}/activate`);
    return response.data;
  }

  /**
   * Изменить статус организации (активировать/заблокировать)
   */
  async updateOrganizationStatus(
    organizationId: number,
    newStatus: string,
    reason: string = "",
  ): Promise<{
    success: boolean;
    organization_id: number;
    old_status: string;
    new_status: string;
  }> {
    const response = await this.client.patch(`/v2/admin/clients/${organizationId}/status`, {
      new_status: newStatus,
      reason,
    });
    return response.data;
  }

  // === ЛК ОРГАНИЗАЦИИ ===

  /**
   * Получить список пользователей организации
   */
  async getOrganizationUsers(): Promise<{
    success: boolean;
    total: number;
    items: Array<{
      id: number;
      email: string;
      last_name?: string;
      first_name?: string;
      middle_name?: string;
      position?: string;
      status: string;
      is_org_admin: boolean;
      totp_enabled: boolean;
      created_at: string;
      last_activity_at?: string;
    }>;
  }> {
    const response = await this.client.get("/v2/organization/users");
    return response.data;
  }

  /**
   * Пригласить пользователя в организацию
   */
  async inviteOrganizationUser(data: {
    email: string;
    last_name: string;
    first_name: string;
    middle_name?: string;
    position?: string;
  }): Promise<{
    success: boolean;
    user_id?: number;
    message?: string;
    error?: string;
  }> {
    const response = await this.client.post("/v2/organization/users/invite", data);
    return response.data;
  }

  /**
   * Деактивировать пользователя организации
   */
  /**
   * Обновить профиль пользователя организации (ФИО + должность).
   */
  async updateOrganizationUser(
    userId: number,
    data: {
      last_name?: string;
      first_name?: string;
      middle_name?: string;
      position?: string;
    },
  ): Promise<{
    success: boolean;
    user?: {
      id: number;
      email: string;
      last_name?: string;
      first_name?: string;
      middle_name?: string;
      position?: string;
      status: string;
      is_org_admin: boolean;
      totp_enabled: boolean;
      created_at: string;
      last_activity_at?: string;
    };
    error?: string;
  }> {
    const response = await this.client.patch(`/v2/organization/users/${userId}`, data);
    return response.data;
  }

  /**
   * Привязать следующий HTTP-запрос к подписанному consent.
   * Используется useNepFlow после успешного ensureConsent — interceptor
   * подставит `X-Consent-Id` ровно один раз. Если запрос не пошёл
   * (caller отменил) — флаг останется до следующего вызова и привяжет
   * не тот запрос. Поэтому caller обязан использовать его непосредственно
   * перед стартом проверки.
   */
  setConsentForNextRequest(consentId: number | null): void {
    _pendingConsentId = consentId;
  }

  async deactivateOrganizationUser(userId: number): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    const response = await this.client.delete(`/v2/organization/users/${userId}`);
    return response.data;
  }

  async activateOrganizationUser(userId: number): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    const response = await this.client.post(`/v2/organization/users/${userId}/activate`);
    return response.data;
  }

  /**
   * Получить бюджет пользователя организации + информацию о резервируемом остатке
   */
  async getUserSpendingLimit(userId: number): Promise<UserSpendingLimitResponse> {
    const response = await this.client.get<UserSpendingLimitResponse>(
      `/v2/organization/users/${userId}/spending-limit`,
    );
    return response.data;
  }

  /**
   * Установить/обновить бюджет пользователя организации
   */
  async setUserSpendingLimit(userId: number, body: UserSpendingLimitBody): Promise<UserSpendingLimitResponse> {
    const response = await this.client.put<UserSpendingLimitResponse>(
      `/v2/organization/users/${userId}/spending-limit`,
      body,
    );
    return response.data;
  }

  /**
   * Снять бюджет пользователя (unlimited, освобождает frozen)
   */
  async deleteUserSpendingLimit(userId: number): Promise<UserSpendingLimitResponse> {
    const response = await this.client.delete<UserSpendingLimitResponse>(
      `/v2/organization/users/${userId}/spending-limit`,
    );
    return response.data;
  }

  /**
   * Сбросить доступ пользователя организации (пароль + TOTP) и
   * отправить ему одноразовую ссылку с новыми данными входа.
   * Доступно только администратору организации.
   */
  async resetOrganizationUserAccess(userId: number): Promise<{
    success: boolean;
    user_id?: number;
    onboarding_link?: string;
    expires_at?: string;
    email_queued?: boolean;
    email_dispatched?: boolean;
    error?: string;
  }> {
    const response = await this.client.post(`/v2/organization/users/${userId}/reset-access`);
    return response.data;
  }

  /**
   * Получить API-ключи текущего пользователя организации
   */
  async getOrganizationApiKeys(): Promise<OrganizationApiKeyListResponse> {
    const response = await this.client.get<OrganizationApiKeyListResponse>("/v2/organization/api-keys");
    return response.data;
  }

  /**
   * Создать API-ключ организации
   */
  async createOrganizationApiKey(data: CreateOrganizationApiKeyRequest): Promise<CreateOrganizationApiKeyResponse> {
    const response = await this.client.post<CreateOrganizationApiKeyResponse>("/v2/organization/api-keys", data);
    return response.data;
  }

  /**
   * Отозвать API-ключ организации
   */
  async revokeOrganizationApiKey(keyId: number, reason: string): Promise<RevokeOrganizationApiKeyResponse> {
    const response = await this.client.post<RevokeOrganizationApiKeyResponse>(
      `/v2/organization/api-keys/${keyId}/revoke`,
      { reason },
    );
    return response.data;
  }

  // === УТИЛИТАРНЫЕ МЕТОДЫ ===

  /**
   * Установка базового URL
   */
  setBaseURL(baseURL: string): void {
    this.baseURL = baseURL;
    this.client.defaults.baseURL = baseURL;
  }

  /**
   * Установка таймаута
   */
  setTimeout(timeout: number): void {
    this.client.defaults.timeout = timeout;
  }

  /**
   * Получение текущего базового URL
   */
  getBaseURL(): string {
    return this.baseURL;
  }

  // === REQUEST HISTORY ===

  /**
   * Получить историю запросов организации
   * @param params - Параметры фильтрации и пагинации
   * @returns Список записей истории с пагинацией
   */
  async getRequestHistory(params: RequestHistoryParams = {}): Promise<RequestHistoryResponse> {
    const queryParams = new URLSearchParams();

    if (params.limit !== undefined) {
      queryParams.append("limit", String(params.limit));
    }
    if (params.offset !== undefined) {
      queryParams.append("offset", String(params.offset));
    }
    if (params.module_keys && params.module_keys.length > 0) {
      params.module_keys.forEach((mk) => queryParams.append("module_key", mk));
    }
    if (params.successes && params.successes.length > 0) {
      params.successes.forEach((s) => queryParams.append("success", String(s)));
    }
    if (params.date_from) {
      queryParams.append("date_from", params.date_from);
    }
    if (params.date_to) {
      queryParams.append("date_to", params.date_to);
    }
    if (params.via_api !== undefined) {
      queryParams.append("via_api", String(params.via_api));
    }
    if (params.target_user_ids && params.target_user_ids.length > 0) {
      params.target_user_ids.forEach((uid) => queryParams.append("target_user_id", String(uid)));
    }

    const queryString = queryParams.toString();
    const url = `/v2/history${queryString ? `?${queryString}` : ""}`;

    const response = await this.client.get<RequestHistoryResponse>(url);
    return response.data;
  }

  /**
   * Получить агрегат истории запросов: разбивка по периоду + module_key.
   * Используется для drill-down вида (год → месяц → неделя → день → час).
   */
  async getRequestHistoryAggregate(params: HistoryAggregateParams): Promise<HistoryAggregateResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append("bucket", params.bucket);
    if (params.date_from) queryParams.append("date_from", params.date_from);
    if (params.date_to) queryParams.append("date_to", params.date_to);
    if (params.module_keys && params.module_keys.length > 0) {
      params.module_keys.forEach((mk) => queryParams.append("module_key", mk));
    }
    if (params.via_api !== undefined) {
      queryParams.append("via_api", String(params.via_api));
    }
    if (params.target_user_ids && params.target_user_ids.length > 0) {
      params.target_user_ids.forEach((uid) => queryParams.append("target_user_id", String(uid)));
    }

    const url = `/v2/history/aggregate?${queryParams.toString()}`;
    const response = await this.client.get<HistoryAggregateResponse>(url);
    return response.data;
  }

  /**
   * Одноразовый просмотр отчёта из истории.
   * Помечает запись как просмотренную и возвращает job_id.
   */
  async viewReportFromHistory(recordId: number): Promise<{
    success: boolean;
    data?: { job_id: string; module_key?: string | null };
    error?: { message: string };
  }> {
    const response = await this.client.post(`/v2/history/${recordId}/view-report`);
    return response.data;
  }

  // === REPORT PDF ===

  /**
   * Сообщает, что пользователь закрыл отчёт: после этого он удаляется. Вызывается
   * и при уходе со страницы, поэтому `keepalive`: обычный запрос браузер оборвёт,
   * а `sendBeacon` не умеет передавать заголовок авторизации.
   */
  acknowledgeReport(jobId: string): void {
    void fetch(`${this.baseURL}/v2/report/complex/${encodeURIComponent(jobId)}/acknowledge`, {
      method: "POST",
      keepalive: true,
      headers: { Authorization: `Bearer ${authService.getToken() ?? ""}` },
    }).catch((err: unknown) => {
      console.warn("Не удалось отметить ознакомление с отчётом:", err);
    });
  }

  /**
   * Запускает генерацию PDF-отчёта.
   */
  async generateReportPdf(jobId: string): Promise<GeneratePdfResponse> {
    const response = await this.client.post<GeneratePdfResponse>(`/v2/report/complex/${jobId}/pdf`);
    return response.data;
  }

  /**
   * Проверяет статус генерации PDF.
   */
  async getReportPdfStatus(jobId: string): Promise<PdfStatusResponse> {
    const response = await this.client.get<PdfStatusResponse>(`/v2/report/complex/${jobId}/pdf/status`);
    return response.data;
  }

  /**
   * Скачивает PDF-отчёт как Blob.
   */
  async downloadReportPdf(jobId: string): Promise<Blob> {
    const response = await this.client.get(`/v2/report/complex/${jobId}/pdf/download`, {
      responseType: "blob",
    });
    return response.data as Blob;
  }

  /**
   * Отправляет PDF-отчёт на email текущего пользователя.
   */
  async sendReportPdfEmail(jobId: string): Promise<SendPdfEmailResponse> {
    const response = await this.client.post<SendPdfEmailResponse>(`/v2/report/complex/${jobId}/pdf/send-email`);
    return response.data;
  }

  // === MODULE PDF (per-module rich PDF — async pipeline) ===

  /**
   * Запускает генерацию PDF для одного модуля.
   *
   * Бэкенд перевыполняет модуль с переданным `requestPayload` (input),
   * генерирует PDF и складывает его в MinIO. Возвращает `pdf_task_id` —
   * клиент опрашивает статус через {@link getModulePdfStatus}.
   */
  async startModulePdf(moduleKey: string, requestPayload: Record<string, unknown>): Promise<ModulePdfCreateResponse> {
    const response = await this.client.post<ModulePdfCreateResponse>(
      `/v2/modules/${encodeURIComponent(moduleKey)}/pdf`,
      requestPayload,
    );
    return response.data;
  }

  /**
   * Опрашивает статус генерации модульного PDF.
   */
  async getModulePdfStatus(pdfTaskId: string): Promise<ModulePdfStatusResponse> {
    const response = await this.client.get<ModulePdfStatusResponse>(
      `/v2/modules/pdf/${encodeURIComponent(pdfTaskId)}/status`,
    );
    return response.data;
  }

  /**
   * Скачивает готовый модульный PDF как Blob (one-time, удаляется в MinIO).
   */
  async downloadModulePdf(pdfTaskId: string): Promise<Blob> {
    const response = await this.client.get(`/v2/modules/pdf/${encodeURIComponent(pdfTaskId)}/download`, {
      responseType: "blob",
    });
    return response.data as Blob;
  }

  // === USER NOTIFICATION SETTINGS ===

  /**
   * Получает настройки уведомлений пользователя.
   */
  async getNotificationSettings(): Promise<NotificationSettingsResponse> {
    const response = await this.client.get<NotificationSettingsResponse>(`/v2/user/settings/notifications`);
    return response.data;
  }

  /**
   * Обновляет настройки уведомлений пользователя.
   */
  async updateNotificationSettings(settings: NotificationSettingsUpdate): Promise<NotificationSettingsResponse> {
    const response = await this.client.patch<NotificationSettingsResponse>(`/v2/user/settings/notifications`, settings);
    return response.data;
  }

  // === USER KEYS (НЭП) ===

  /** Список ключей пользователя (включая отозванные). */
  async getUserKeys(): Promise<UserKeyListResponse> {
    const response = await this.client.get<UserKeyListResponse>("/v2/platform/keys");
    return response.data;
  }

  /** Активный ключ текущего пользователя или null если нет. */
  async getActiveUserKey(): Promise<UserKey | null> {
    const response = await this.client.get<UserKey | null>("/v2/platform/keys/active");
    return response.data;
  }

  /** Сгенерировать новую RSA-пару (приватник заворачивается KMS). */
  async generateUserKey(data: GenerateUserKeyRequest = {}): Promise<GenerateUserKeyResponse> {
    const response = await this.client.post<GenerateUserKeyResponse>("/v2/platform/keys/generate", data);
    return response.data;
  }

  /** Отозвать ключ. */
  async revokeUserKey(keyId: number, reason: string): Promise<RevokeUserKeyResponse> {
    const response = await this.client.post<RevokeUserKeyResponse>(`/v2/platform/keys/${keyId}/revoke`, {
      reason,
    });
    return response.data;
  }

  // === CONSENTS (НЭП) ===

  /** Список согласий пользователя с фильтрами. */
  async getConsents(params?: { consent_type?: ConsentType; status?: ConsentStatus }): Promise<ConsentListResponse> {
    const response = await this.client.get<ConsentListResponse>("/v2/platform/consents", { params });
    return response.data;
  }

  /** Получить конкретное согласие. */
  async getConsent(consentId: number): Promise<Consent> {
    const response = await this.client.get<Consent>(`/v2/platform/consents/${consentId}`);
    return response.data;
  }

  /** Подписать согласие НЭП. Требует step-up TOTP-код. */
  async signConsent(data: SignConsentRequest): Promise<SignConsentResponse> {
    const response = await this.client.post<SignConsentResponse>("/v2/platform/consents/sign", data);
    return response.data;
  }

  /** Верифицировать подпись согласия. */
  async verifyConsent(consentId: number): Promise<VerifyConsentResponse> {
    const response = await this.client.post<VerifyConsentResponse>(`/v2/platform/consents/${consentId}/verify`, {});
    return response.data;
  }

  /** Отозвать согласие. Требует step-up TOTP-код. */
  async revokeConsent(consentId: number, totpCode: string): Promise<RevokeConsentResponse> {
    const body: RevokeConsentRequest = { totp_code: totpCode };
    const response = await this.client.post<RevokeConsentResponse>(`/v2/platform/consents/${consentId}/revoke`, body);
    return response.data;
  }

  /**
   * Phase 5.2 MR2d.4: проверить наличие активного согласия для субъекта.
   *
   * Используется как guard перед запуском проверки/подписки.
   * Backend сравнивает по 3 fingerprint: ФИО+ДР, ИНН, СНИЛС — достаточно
   * совпадения по любому. Возвращает matched_by для отладки и fingerprints
   * для последующей записи (если нужно подписать).
   */
  async checkConsent(data: CheckConsentRequest): Promise<CheckConsentResponse> {
    const response = await this.client.post<CheckConsentResponse>("/v2/platform/consents/check", data);
    return response.data;
  }

  /** Phase 5.2 MR2d.4: загрузить скан бумажного согласия в MinIO. */
  async uploadConsentScan(consentId: number, file: File): Promise<ConsentScanInfo> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await this.client.post<ConsentScanInfo>(`/v2/platform/consents/${consentId}/scan`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  /** Phase 5.2 MR2d.4: получить presigned URL для скачивания скана. TTL 15 мин. */
  async getConsentScanUrl(consentId: number): Promise<ConsentScanDownloadResponse> {
    const response = await this.client.get<ConsentScanDownloadResponse>(`/v2/platform/consents/${consentId}/scan`);
    return response.data;
  }

  /** Phase 5.2 MR2d.4: удалить ранее загруженный скан. */
  async deleteConsentScan(consentId: number): Promise<ConsentScanDeleteResponse> {
    const response = await this.client.delete<ConsentScanDeleteResponse>(`/v2/platform/consents/${consentId}/scan`);
    return response.data;
  }

  // === MONITORING SUBSCRIPTIONS ===

  /** Список подписок организации. */
  async getMonitoringSubscriptions(params?: {
    status?: MonitoringSubscriptionStatus;
    limit?: number;
    offset?: number;
  }): Promise<MonitoringSubscriptionListResponse> {
    const response = await this.client.get<MonitoringSubscriptionListResponse>("/v2/monitoring/subscriptions", {
      params,
    });
    return response.data;
  }

  /** Подписка по id. */
  async getMonitoringSubscription(subscriptionId: string): Promise<MonitoringSubscription> {
    const response = await this.client.get<MonitoringSubscription>(`/v2/monitoring/subscriptions/${subscriptionId}`);
    return response.data;
  }

  /** Создать новую подписку на мониторинг. */
  async createMonitoringSubscription(data: CreateMonitoringSubscriptionRequest): Promise<MonitoringSubscription> {
    const response = await this.client.post<MonitoringSubscription>("/v2/monitoring/subscriptions", data);
    return response.data;
  }

  /** Поставить криптоадрес на мониторинг; повтор того же адреса — 400. */
  async createCryptoMonitoringSubscription(
    data: CreateCryptoMonitoringRequest,
  ): Promise<CreatedMonitoringSubscription> {
    const response = await this.client.post<CreatedMonitoringSubscription>("/v2/monitoring/subscriptions", data);
    return response.data;
  }

  /** Обновить подписку (расписание, каналы, severity и т.д.). */
  async updateMonitoringSubscription(
    subscriptionId: string,
    data: UpdateMonitoringSubscriptionRequest,
  ): Promise<MonitoringSubscription> {
    const response = await this.client.patch<MonitoringSubscription>(
      `/v2/monitoring/subscriptions/${subscriptionId}`,
      data,
    );
    return response.data;
  }

  /** Удалить подписку (триггерит SHRED PII). */
  async deleteMonitoringSubscription(subscriptionId: string): Promise<void> {
    await this.client.delete(`/v2/monitoring/subscriptions/${subscriptionId}`);
  }

  /**
   * Phase 5.2 MR2d.5: возобновить PAUSED-подписку новым согласием.
   *
   * Ставится после re-sign consent: backend проверит, что новое согласие
   * активно и принадлежит тому же пользователю, и переведёт подписку из
   * PAUSED в ACTIVE с обновлённым consent_id и пересчитанным next_run_at.
   */
  async resumeMonitoringSubscriptionWithConsent(
    subscriptionId: string,
    newConsentId: number,
  ): Promise<MonitoringSubscription> {
    const response = await this.client.patch<MonitoringSubscription>(
      `/v2/monitoring/subscriptions/${subscriptionId}/resume-with-consent`,
      { new_consent_id: newConsentId },
    );
    return response.data;
  }

  /**
   * Ставит немедленный run в очередь TaskIQ. Endpoint возвращает 202 сразу
   * после постановки; результат прогона тянется отдельно через listSubscriptions
   * + getMonitoringLastRun. До этого endpoint был sync и легко выходил за axios
   * timeout 120s при 30+ модулях с web scraping.
   */
  async runMonitoringSubscriptionNow(subscriptionId: string): Promise<MonitoringRunQueuedResponse> {
    const response = await this.client.post<MonitoringRunQueuedResponse>(
      `/v2/monitoring/subscriptions/${subscriptionId}/run-now`,
      {},
    );
    return response.data;
  }

  /** События подписки. */
  async getMonitoringEvents(
    subscriptionId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<MonitoringEventsListResponse> {
    const response = await this.client.get<MonitoringEventsListResponse>(
      `/v2/monitoring/subscriptions/${subscriptionId}/events`,
      { params },
    );
    return response.data;
  }

  /**
   * Детали последнего прогона подписки (полный результат каждого модуля).
   * Архив прогонов не ведётся — отдаёт только последний.
   */
  async getMonitoringLastRun(subscriptionId: string): Promise<MonitoringLastRunResponse> {
    const response = await this.client.get<MonitoringLastRunResponse>(
      `/v2/monitoring/subscriptions/${subscriptionId}/last-run`,
    );
    return response.data;
  }

  /**
   * Отчёт по последнему прогону подписки (PersonReport-стиль).
   * UI рендерит секциями /360: basic_information + risk_assessment +
   * per-module данные.
   */
  async getMonitoringLastRunReport(subscriptionId: string): Promise<MonitoringLastRunReportResponse> {
    const response = await this.client.get<MonitoringLastRunReportResponse>(
      `/v2/monitoring/subscriptions/${subscriptionId}/last-run-report`,
    );
    return response.data;
  }

  // ─── Risk History (п.7.6 договора) ───────────────────────────────────────

  /**
   * История обезличенных риск-профилей по идентификационным данным субъекта.
   * Поиск OR-матчингом по хешам ФИО+ДР / ИНН / СНИЛС — атомарные снапшоты
   * (например ЕФРСБ только по СНИЛС) подтянутся при полном поиске.
   */
  async getRiskHistoryByPerson(query: RiskHistoryPersonQuery): Promise<RiskHistoryResponse> {
    const response = await this.client.post<RiskHistoryResponse>("/v2/risk-history/by-person", query);
    return response.data;
  }

  /** Heartbeat-история риск-профиля по подписке мониторинга. */
  async getRiskHistoryBySubscription(
    subscriptionId: string,
    params?: { from?: string; to?: string; limit?: number },
  ): Promise<RiskHistoryResponse> {
    const response = await this.client.get<RiskHistoryResponse>(`/v2/risk-history/monitoring/${subscriptionId}`, {
      params,
    });
    return response.data;
  }

  // ===== Risk Matrices (настраиваемые матрицы рисков) =====

  async getRiskMatrices(): Promise<RiskMatrixSummary[]> {
    const response = await this.client.get<RiskMatrixSummary[]>("/v2/risk-matrices");
    return response.data;
  }

  async getRiskMatrixSchema(): Promise<RiskMatrixSchema> {
    const response = await this.client.get<RiskMatrixSchema>("/v2/risk-matrices/schema");
    return response.data;
  }

  async getRiskMatrix(id: string): Promise<RiskMatrixDetail> {
    const response = await this.client.get<RiskMatrixDetail>(`/v2/risk-matrices/${id}`);
    return response.data;
  }

  async createRiskMatrix(data: CreateRiskMatrixRequest): Promise<RiskMatrixDetail> {
    const response = await this.client.post<RiskMatrixDetail>("/v2/risk-matrices", data);
    return response.data;
  }

  async updateRiskMatrix(id: string, data: UpdateRiskMatrixRequest): Promise<RiskMatrixDetail> {
    const response = await this.client.put<RiskMatrixDetail>(`/v2/risk-matrices/${id}`, data);
    return response.data;
  }

  async deleteRiskMatrix(id: string): Promise<void> {
    await this.client.delete(`/v2/risk-matrices/${id}`);
  }

  async setDefaultRiskMatrix(id: string): Promise<RiskMatrixSummary> {
    const response = await this.client.post<RiskMatrixSummary>(`/v2/risk-matrices/${id}/set-default`, {});
    return response.data;
  }

  // ===== Admin Risk Matrices (платформенные пресеты, только staff) =====

  async getAdminRiskMatrices(): Promise<RiskMatrixSummary[]> {
    const response = await this.client.get<RiskMatrixSummary[]>("/v2/admin/risk-matrices");
    return response.data;
  }

  async getAdminRiskMatrixSchema(): Promise<RiskMatrixSchema> {
    const response = await this.client.get<RiskMatrixSchema>("/v2/admin/risk-matrices/schema");
    return response.data;
  }

  async getAdminRiskMatrix(id: string): Promise<RiskMatrixDetail> {
    const response = await this.client.get<RiskMatrixDetail>(`/v2/admin/risk-matrices/${id}`);
    return response.data;
  }

  async createAdminRiskMatrix(data: CreateRiskMatrixRequest): Promise<RiskMatrixDetail> {
    const response = await this.client.post<RiskMatrixDetail>("/v2/admin/risk-matrices", data);
    return response.data;
  }

  async updateAdminRiskMatrix(id: string, data: UpdateRiskMatrixRequest): Promise<RiskMatrixDetail> {
    const response = await this.client.put<RiskMatrixDetail>(`/v2/admin/risk-matrices/${id}`, data);
    return response.data;
  }

  async deleteAdminRiskMatrix(id: string): Promise<void> {
    await this.client.delete(`/v2/admin/risk-matrices/${id}`);
  }

  async setAdminRiskMatrixDefault(id: string): Promise<RiskMatrixSummary> {
    const response = await this.client.post<RiskMatrixSummary>(`/v2/admin/risk-matrices/${id}/set-default`, {});
    return response.data;
  }
}

// Создаем экземпляр сервиса

/** Поле входных данных модуля в паспорте реестра. */
export interface CatalogModuleField {
  name: string;
  display_name?: string;
  description?: string;
  /** Тип значения: string, date, boolean, integer, tax_id, snils, phone, email. */
  type?: string;
  required?: boolean;
  /** Значение по умолчанию (у bool-флагов, включённых по умолчанию, — true). */
  default?: unknown;
  /** Обязательное подтверждение: принимается только true (согласия субъекта). */
  must_be_true?: boolean;
}

/** Паспорт модуля из GET /v2/modules. */
export interface CatalogModule {
  key: string;
  category: string;
  name: string;
  description?: string;
  required_fields?: CatalogModuleField[] | null;
  optional_fields?: CatalogModuleField[] | null;
  /** Юрисдикции модуля (ISO alpha-2, «*» — внестрановой). Пусто → Россия. */
  jurisdictions?: string[] | null;
  enabled?: boolean;
}

const apiService = new ApiService(getApiBaseUrl());

export default apiService;
export { ApiService };
